/**
 * PipelineDetailPage — Heroku-style 5-tab view of one deployed workflow.
 *
 * Mounted at /projects/:projectId/pipelines/:workflowType. Renders inside
 * ProjectLayout so sidebar + project context come from the parent.
 *
 * Tabs:
 *   Overview  — UUID, status, version, CLI commands to trigger
 *   Activity  — past executions of this workflow (DataTable)
 *   Logs      — pointer to `zibby workflow logs -t <uuid>` (no live SSE
 *               viewer in the dashboard yet — CLI is the canonical surface
 *               and we don't reimplement it badly)
 *   Settings  — repo / trigger config (placeholder for v1; reads workflow
 *               row + lets user delete)
 *   Env       — environment variable KEYS (values never returned from the
 *               server, by design — see backend/handlers/workflow-env.js)
 *
 * No new components — uses DataTable + plain divs styled in CSS.
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import PipelineStatus from './PipelineStatus';
import { useParams, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { trashOutline, copyOutline, documentTextOutline, createOutline, checkmarkOutline, closeOutline, sparklesOutline, cloudUploadOutline, cubeOutline, arrowBackOutline, addOutline, flashOutline, eyeOutline, eyeOffOutline } from 'ionicons/icons';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import { IonIcon, IonButton, IonSpinner, IonToggle } from '@ionic/react';
import DataTable from '../../components/shared/DataTable/DataTable';
import ExecutionPipeline from '../../components/shared/ExecutionPipeline';
import api from '../../services/api';
import WorkflowLogs from '../../services/workflowLogs';
import Addons from '../../services/addons';
import Credits, { formatBalance, formatRate } from '../../services/credits';
import InsufficientCreditsModal from '../../components/billing/InsufficientCreditsModal';
import SplitButton from '../../components/shared/SplitButton/SplitButton';
import DangerButton from '../../components/shared/DangerButton/DangerButton';
import DangerZone from '../../components/shared/DangerZone/DangerZone';
import LogLine, { parseLogPayload } from '../../components/shared/LogLine';
import PillButton from '../../components/shared/PillButton';
import AuthCard from '../../components/shared/AuthCard/AuthCard';
import MktAlertModal from '../../components/molecules/MktAlertModal/MktAlertModal';
import { WARM_POOL_QUANTITY_OPTIONS } from '../../config/warmPool';
import { AGENT_OPTIONS, encodeAgentOption, useAgentOptions, findAgentOptionValue } from '../../utils/agent-options';
import Marketplace from '../../services/marketplace';
import { timeAgo } from '../../utils/timeAgo';
import { CronExpressionParser } from 'cron-parser';
import cronstrue from 'cronstrue';
import usageService from '../../services/usage';
import TriggerModal from './TriggerModal';
import AgentModelModal from '../../components/molecules/AgentModelModal/AgentModelModal';
import './PipelineDetailPage.css';
// Inline run-detail header reuses the standalone PipelineRunDetailPage's
// chrome (status pill / meta row / copy button). Importing its CSS
// keeps that surface DRY — same look on either render path.
import './PipelineRunDetailPage.css';
// The Logs tab reuses the app detail page's `.app-logs-*` log-viewer CSS
// (dark monospace buffer, line numbers, severity stripes, skeleton). That
// stylesheet is self-contained — importing it here keeps the two log
// surfaces visually identical without duplicating the rules.
import '../AppsPage/AppDetailPage.css';
// The webhook AUTHENTICATION methods are rendered as the SAME card the
// Billing page uses for "Dedicated Static IP" / "Warm Pool" (title left,
// description under, Enable/Disable action top-right). We mirror those
// classNames here so the cards look identical — importing the stylesheet
// keeps that surface DRY rather than re-declaring the rules.
import '../../components/billing/AddonCard.css';

const TERMINAL_STATUSES = new Set(['completed', 'success', 'succeeded', 'failed', 'cancelled', 'skipped', 'blocked', 'insufficient_context']);

/**
 * HelpIcon — tiny question-mark icon next to a section title. Hover
 * reveals the full description as a tooltip. Trades discoverability of
 * always-visible help text for a less cluttered surface — once a user
 * has read the hint once they don't need to re-read it on every visit.
 *
 * CSS-only hover. The `data-hint` attribute is rendered into the
 * ::after pseudo-element via the rule in PipelineDetailPage.css.
 */
function HelpIcon({ hint }) {
  return (
    <span
      className="pipeline-help-icon"
      data-hint={hint}
      tabIndex={0}
      role="tooltip"
      aria-label={hint}
      title={hint /* native title is a fallback for browsers that strip ::after */}
    >
      <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
        <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.3" />
        <text x="8" y="11.5" textAnchor="middle" fontSize="9" fontWeight="600" fill="currentColor">?</text>
      </svg>
    </span>
  );
}

/**
 * ScheduleStatusPanel — live display of a workflow's cron schedule.
 *
 * Fetches GET /workflows/{uuid}/schedule on mount, refreshes when the
 * workflow uuid changes, and renders the attached schedule (if any)
 * with a ticking countdown to the next firing.
 *
 * When no schedule is attached we render nothing — the helper CLI rows
 * below ("set / show / clear") are the call to action. When a schedule
 * IS attached, this panel sits above the helpers so the user sees
 * "what's running" before "how to change it".
 */
function ScheduleStatusPanel({ workflowUuid }) {
  const [schedule, setSchedule]   = useState(null);
  const [loading,  setLoading]    = useState(true);
  const [error,    setError]      = useState(null);
  const [now,      setNow]        = useState(() => new Date());
  const [usage,    setUsage]      = useState(null);

  // Fetch the schedule. Tied to workflowUuid — re-runs if the user
  // navigates to a different pipeline.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/workflows/${workflowUuid}/schedule`)
      .then((data) => {
        if (cancelled) return;
        setSchedule(data?.schedule || null);
      })
      .catch((err) => {
        if (cancelled) return;
        // 404 = no schedule yet (valid state). Anything else is real failure.
        if (err?.status === 404 || /404/.test(String(err?.message || ''))) {
          setSchedule(null);
        } else {
          setError(err.message || 'Failed to load schedule');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workflowUuid]);

  // Fetch usage in parallel with the schedule. When the account is out
  // of execution quota, the backend's scheduled-trigger Lambda silently
  // skips firing (returns 429); we surface that here so the user sees
  // "won't fire" rather than "next run in 30s" that never actually runs.
  useEffect(() => {
    let cancelled = false;
    usageService.getUsage()
      .then((data) => {
        if (cancelled) return;
        const used  = Number(data?.workflowExecutionsUsed)  || 0;
        const limit = Number(data?.workflowExecutionsLimit) || 0;
        setUsage({ used, limit, exceeded: limit > 0 && used >= limit });
      })
      .catch(() => { /* usage fetch is best-effort — don't block schedule UI */ });
    return () => { cancelled = true; };
  }, [workflowUuid]);

  // 1 Hz tick — drives the countdown without re-fetching the schedule.
  useEffect(() => {
    if (!schedule) return undefined;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [schedule]);

  // Compute next firing time from cron + timezone. cron-parser handles
  // the cron arithmetic + IANA timezones. Memoize on cron+timezone so
  // we don't re-parse every second's setNow tick.
  const nextRun = useMemo(() => {
    if (!schedule?.cron) return null;
    try {
      const interval = CronExpressionParser.parse(schedule.cron, {
        currentDate: new Date(),
        tz: schedule.timezone || 'UTC',
      });
      return interval.next().toDate();
    } catch (err) {
      console.warn('[schedule] cron parse failed:', err.message);
      return null;
    }
    // We deliberately don't depend on `now` — recomputing once per
    // mount is plenty. The displayed countdown delta uses `now`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule?.cron, schedule?.timezone, schedule?.updatedAt]);

  // Loading / error → silent. An error banner at the top of every page
  // surfaces infra issues users can't act on.
  if (loading || error) return null;

  // No schedule attached: render a slim "no schedule" indicator so the
  // user knows nothing IS attached (vs panel broken / wondering "did
  // it get deleted?"). The CTA is the helpers section below.
  if (!schedule) {
    return (
      <div className="pipeline-section">
        <h3 className="pipeline-section-title">
          Active schedule
          <HelpIcon hint={'Currently no cron is attached to this agent. Set one with `zibby workflow schedule <uuid> set "0 9 * * 1-5"` below.'} />
        </h3>
        <p className="pipeline-section-hint" style={{ opacity: 0.65, fontStyle: 'italic' }}>
          No schedule attached. Use <code>zibby workflow schedule</code> below to add one.
        </p>
      </div>
    );
  }

  const deltaMs = nextRun ? Math.max(0, nextRun.getTime() - now.getTime()) : null;
  const formatDelta = (ms) => {
    if (ms === null) return '—';
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
  };

  // Human-readable cron via cronstrue. Falls back to the raw expression
  // if cronstrue can't parse (shouldn't happen — backend validates on POST).
  let cronDescription = null;
  try {
    cronDescription = cronstrue.toString(schedule.cron, {
      use24HourTimeFormat: false,
      verbose: false,
    });
  } catch { /* malformed cron — just don't show description */ }

  // Quota state — when the account is over its monthly workflow-execution
  // limit, the scheduled-trigger Lambda short-circuits and the schedule
  // won't actually fire even though EventBridge keeps ticking. Reflect
  // that in the UI: freeze the countdown and warn next to "Next run".
  const quotaExceeded = !!usage?.exceeded;
  const quotaTooltip = quotaExceeded
    ? `Out of agent-execution quota for this billing period (${usage.used}/${usage.limit}). Schedule will keep ticking in AWS but the run will be skipped on each fire until quota resets or you upgrade.`
    : null;

  return (
    <div className="pipeline-section">
      <h3 className="pipeline-section-title">
        Active schedule
        <HelpIcon hint="The cron currently attached to this agent. EventBridge fires on this schedule; each fire counts toward your agent-execution quota. Edit with the CLI commands below." />
      </h3>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '12px',
        padding: '12px',
        background: 'rgba(255, 255, 255, 0.03)',
        borderRadius: '6px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
      }}>
        <div>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.6, letterSpacing: '0.05em' }}>Cron</div>
          <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '14px', marginTop: '4px' }}>
            {schedule.cron}
          </div>
          {cronDescription && (
            <div style={{ fontSize: '12px', marginTop: '2px', opacity: 0.7 }}>
              {cronDescription}
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.6, letterSpacing: '0.05em' }}>Timezone</div>
          <div style={{ fontSize: '14px', marginTop: '4px' }}>{schedule.timezone || 'UTC'}</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.6, letterSpacing: '0.05em', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            Next run
            {quotaExceeded && (
              <span
                className="pipeline-help-icon"
                data-hint={quotaTooltip}
                style={{ color: '#f1b94c' }}
                tabIndex={0}
                role="tooltip"
                aria-label={quotaTooltip}
                title={quotaTooltip}
              >
                <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
                  <path d="M8 1.5 L15 14 H1 Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
                  <line x1="8" y1="6" x2="8" y2="10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                  <circle cx="8" cy="12" r="0.9" fill="currentColor" />
                </svg>
              </span>
            )}
          </div>
          <div style={{ fontSize: '14px', marginTop: '4px', textDecoration: quotaExceeded ? 'line-through' : undefined, opacity: quotaExceeded ? 0.55 : 1 }}>
            {nextRun ? nextRun.toLocaleString() : '—'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.6, letterSpacing: '0.05em' }}>Countdown</div>
          <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '14px', marginTop: '4px', color: quotaExceeded ? '#f1b94c' : (deltaMs !== null && deltaMs < 60_000 ? '#7fdc7f' : undefined) }}>
            {quotaExceeded ? 'Paused (no quota)' : formatDelta(deltaMs)}
          </div>
        </div>
      </div>
      {schedule.input && Object.keys(schedule.input).length > 0 && (
        <p className="pipeline-section-hint" style={{ marginTop: 8 }}>
          <strong>Fixed input:</strong> <code>{JSON.stringify(schedule.input)}</code>
        </p>
      )}
      <p className="pipeline-section-hint" style={{ marginTop: 8, fontSize: '12px', opacity: 0.7 }}>
        Set on {schedule.createdAt ? new Date(schedule.createdAt).toLocaleString() : 'unknown'}
        {schedule.updatedAt && schedule.updatedAt !== schedule.createdAt
          ? ` · last updated ${new Date(schedule.updatedAt).toLocaleString()}`
          : ''}
      </p>
    </div>
  );
}

const TABS = [
  { id: 'overview', label: 'Overview' },
  // Logs — paginated tail of the workflow's container logs (the execution
  // log group `/ecs/zibby-agent-<stage>`, which also carries warm-runner
  // warm-up output). Served by the workflow-logs subdomain RestApi, NOT the
  // main API host — see services/workflowLogs.js. The CLI's `-t` live SSE
  // tail is still the canonical real-time surface; this is the historical
  // browse view that survives sleeps (CloudWatch retains the events).
  { id: 'logs',     label: 'Logs' },
  { id: 'activity', label: 'Activity' },
  { id: 'settings', label: 'Settings' },
  { id: 'env',      label: 'Env' },
];

const PipelineDetailPage = () => {
  const { projectId, workflowType, executionId } = useParams();
  const navigate = useNavigate();
  const { setToast } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  // Live agent:model SSOT picker options for the Agent & model modal.
  const { options: agentOptions } = useAgentOptions();

  const [pipeline, setPipeline] = useState(null);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  // Warm enabled but the runner isn't actually RUNNING yet → the header shows
  // "Starting" (not "Live"). Polled via the warm-pool status endpoint until
  // the runner is up. See the effect below.
  const [warmProvisioning, setWarmProvisioning] = useState(false);
  // Marketplace "Update available" — open the confirm modal; force-deploy
  // POST is wrapped in setUpdating so the button shows a spinner.
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Trigger modal — collects { input } per workflow.graph.stateSchema and
  // POSTs to the shared trigger endpoint (same gate as the CLI). On success
  // we navigate to the run page so the Activity tab's existing polling
  // picks up live status without an extra hop.
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  // Per-pipeline agent + model + token editor.
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const handleTriggered = useCallback((jobId) => {
    setTriggerModalOpen(false);
    navigate(`/projects/${projectId}/pipelines/${workflowType}/runs/${jobId}`);
  }, [navigate, projectId, workflowType]);

  // Inline edit state for displayName (the pencil icon next to the
  // workflow title). workflowType — the URL slug — stays immutable:
  // renaming it would break per-UUID links, sub-graph references, and
  // the subdomain registration. displayName is the only human-friendly
  // override; defaults to workflowType when absent.
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const nameInputRef = useRef(null);

  const tabFromUrl = searchParams.get('tab');
  // When the URL carries an executionId (deep-link to a specific run),
  // force the Activity tab — that's where the inline run detail panel
  // renders. Tab querystring only matters when no executionId is set.
  const activeTab = executionId
    ? 'activity'
    : TABS.some((t) => t.id === tabFromUrl) ? tabFromUrl : 'overview';
  // Clicking a tab when a run is open: navigate back to the plain
  // workflow URL with that tab active. Without this, clicking
  // Overview/Settings/Env did nothing — the executionId override
  // pinned the tab back to Activity.
  const setActiveTab = (id) => {
    if (executionId) {
      navigate(`/projects/${projectId}/pipelines/${workflowType}?tab=${id}`);
    } else {
      setSearchParams({ tab: id }, { replace: true });
    }
  };

  const fetchPipeline = useCallback(async ({ silent = false } = {}) => {
    // `silent` refetches in the background without flipping the page-level
    // skeleton — used after in-place actions (e.g. enabling warm pool) so
    // the whole page doesn't flash just to update the header status pill.
    // The full-page skeleton is reserved for the initial load.
    if (!silent) setPipelineLoading(true);
    try {
      const data = await api.get(`/projects/${projectId}/workflows/${workflowType}`);
      setPipeline(data);
    } catch (err) {
      // 404 = workflow doesn't exist (and isn't a built-in type or
      // marketplace template). Fall through to the NotFoundPage render
      // branch by leaving pipeline at null — no toast, no error log,
      // it's a normal user-facing state.
      if (err?.status === 404 || /404/.test(String(err?.message || ''))) {
        setPipeline(null);
        return;
      }
      console.error('Failed to fetch pipeline:', err);
      setToast?.({ show: true, message: `Could not load agent: ${err.message}`, color: 'danger' });
    } finally {
      if (!silent) setPipelineLoading(false);
    }
  }, [projectId, workflowType, setToast]);

  // App-Store-style "Update" — only meaningful when the backend overlay
  // says updateAvailable=true. Calls deploy with force=true, which
  // re-copies the latest bundle into the same workflow row preserving
  // env vars, triggers, displayName, and warm config (server-side).
  const handleUpdate = useCallback(async () => {
    const slug = pipeline?.marketplace?.slug;
    if (!slug || updating) return;
    setUpdating(true);
    try {
      await Marketplace.deployWorkflow(slug, projectId, {
        workflowName: workflowType,
        force: true,
      });
      setUpdateModalOpen(false);
      setToast?.({
        show: true,
        message: `Updated to v${pipeline.marketplace.currentVersion}`,
        color: 'success',
      });
      // Re-fetch so the pill clears + Last deployed timestamp ticks.
      await fetchPipeline();
    } catch (err) {
      console.error('Update failed:', err);
      setToast?.({
        show: true,
        message: `Update failed: ${err.message || 'unknown error'}`,
        color: 'danger',
      });
    } finally {
      setUpdating(false);
    }
  }, [pipeline, projectId, workflowType, updating, fetchPipeline, setToast]);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  // Header "Starting" → "Live": when this pipeline is warm-enabled, ask the
  // warm-pool status endpoint whether a runner is actually RUNNING yet. While
  // it's still provisioning, keep polling so the header flips on its own once
  // the runner is up — without a manual refresh. Best-effort: any error clears
  // the flag so the header never gets stuck on "Starting".
  useEffect(() => {
    if (!pipeline?.warmEnabled) { setWarmProvisioning(false); return undefined; }
    let cancelled = false;
    let timer = null;
    const check = async () => {
      try {
        const data = await Addons.getWarmPoolStatus({ projectId, workflowType });
        if (cancelled) return;
        const provisioning = data?.enabled === true && data?.provisioning === true;
        setWarmProvisioning(provisioning);
        if (provisioning) timer = setTimeout(check, 5000);
      } catch {
        if (!cancelled) setWarmProvisioning(false);
      }
    };
    check();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [pipeline?.warmEnabled, projectId, workflowType]);

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text).then(() => {
      setToast?.({ show: true, message: 'Copied to clipboard', color: 'success' });
    });
  };

  const handleStartRename = () => {
    setNameDraft(pipeline?.displayName || workflowType || '');
    setEditingName(true);
    // Defer focus to after paint so the input is actually mounted.
    setTimeout(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }, 0);
  };

  const handleCancelRename = () => {
    setEditingName(false);
    setNameDraft('');
  };

  const handleSaveName = async () => {
    if (!pipeline?.uuid) {
      setToast?.({ show: true, message: 'Cannot rename: agent has no UUID yet', color: 'danger' });
      setEditingName(false);
      return;
    }
    const trimmed = nameDraft.trim();
    if (trimmed.length > 80) {
      setToast?.({ show: true, message: 'Display name must be 80 characters or fewer', color: 'danger' });
      return;
    }
    setSavingName(true);
    try {
      // Empty string clears displayName (server falls back to
      // workflowType when absent). Non-empty values get trimmed before
      // submission so leading/trailing whitespace can't be persisted
      // (server enforces this too — defense in depth).
      const body = { displayName: trimmed === '' ? null : trimmed };
      await api.patch(`/workflows/${pipeline.uuid}`, body);
      setPipeline((prev) => prev ? { ...prev, displayName: trimmed === '' ? undefined : trimmed } : prev);
      setEditingName(false);
      setToast?.({ show: true, message: 'Agent name updated', color: 'success' });
    } catch (err) {
      setToast?.({ show: true, message: `Rename failed: ${err.message || 'unknown error'}`, color: 'danger' });
    } finally {
      setSavingName(false);
    }
  };

  // Confirmation is handled by the styled DangerZone modal — this just
  // performs the delete (called as the modal's onConfirm).
  const handleDelete = async () => {
    if (!pipeline?.uuid) {
      setToast?.({ show: true, message: 'Cannot delete: agent has no UUID yet (deploy it first)', color: 'danger' });
      return;
    }
    try {
      // Backend wires DELETE on `/workflows/{uuid}`; the handler looks
      // up the workflow via UUID GSI and verifies project membership
      // server-side, so projectId is implied.
      await api.delete(`/workflows/${pipeline.uuid}`);
      setToast?.({ show: true, message: `Agent "${workflowType}" deleted`, color: 'success' });
      navigate(`/projects/${projectId}/pipelines`);
    } catch (err) {
      setToast?.({ show: true, message: `Delete failed: ${err.message}`, color: 'danger' });
    }
  };

  // ── render helpers ─────────────────────────────────────────────────

  if (pipelineLoading) {
    return (
      <div className="pipeline-detail-page" aria-busy="true" aria-label="Loading agent">
        <div className="pipeline-detail-skel-header">
          <div className="pipeline-detail-skel pipeline-detail-skel--title" />
          <div className="pipeline-detail-skel pipeline-detail-skel--meta" />
        </div>
        <div className="pipeline-detail-skel-tabs">
          <div className="pipeline-detail-skel pipeline-detail-skel--tab" />
          <div className="pipeline-detail-skel pipeline-detail-skel--tab" />
          <div className="pipeline-detail-skel pipeline-detail-skel--tab" />
        </div>
        <div className="pipeline-detail-skel-body">
          <div className="pipeline-detail-skel pipeline-detail-skel--row" />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '85%' }} />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '60%' }} />
        </div>
      </div>
    );
  }

  // Two empty states, both routed through the shared NotFoundPage:
  //   1. pipeline === null         → workflow row not in DB AND no
  //                                   marketplace slug match → genuine 404
  //   2. pipeline.notInstalled     → marketplace template exists with
  //                                   this slug but it's not deployed
  //                                   to this project (backend returns
  //                                   a schema-only preview payload so
  //                                   trigger modals work; the rest of
  //                                   the detail page would render with
  //                                   undefined UUID / version / dates
  //                                   which is the "fake page" trap)
  if (!pipeline || pipeline.notInstalled === true) {
    const isMarketplace = pipeline?.notInstalled === true;
    const slug = pipeline?.marketplaceSlug || workflowType;
    return (
      <NotFoundPage
        code="404"
        title={isMarketplace
          ? `'${workflowType}' isn't deployed here yet`
          : 'Agent not found'}
        subtitle={isMarketplace
          ? "This template lives in the marketplace but hasn't been deployed to this project. Install it to start triggering runs."
          : "This agent doesn't exist, or it may have been deleted. Check the URL or browse your project's agents."}
        primaryAction={isMarketplace
          ? { label: 'Install from marketplace', to: `/projects/${projectId}/marketplace?slug=${encodeURIComponent(slug)}`, icon: cubeOutline }
          : { label: 'Back to agents', to: `/projects/${projectId}/pipelines`, icon: arrowBackOutline }
        }
        secondaryAction={isMarketplace
          ? { label: 'Back to agents', to: `/projects/${projectId}/pipelines`, icon: arrowBackOutline }
          : { label: 'Browse marketplace', to: `/projects/${projectId}/marketplace`, icon: cubeOutline }
        }
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>{(pipeline?.displayName || workflowType)} · Agents · Zibby</title>
      </Helmet>
      <div className="pipeline-detail-page">
        <div className="pipeline-detail-header">
          <div className="pipeline-detail-titlerow">
            {editingName ? (
              <div className="pipeline-detail-title-edit">
                <input
                  ref={nameInputRef}
                  type="text"
                  className="pipeline-detail-title-input"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveName();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      handleCancelRename();
                    }
                  }}
                  maxLength={80}
                  placeholder={workflowType}
                  disabled={savingName}
                  aria-label="Agent display name"
                />
                <button
                  type="button"
                  className="pipeline-detail-title-btn pipeline-detail-title-save"
                  onClick={handleSaveName}
                  disabled={savingName}
                  aria-label="Save"
                  title="Save (Enter)"
                >
                  <IonIcon icon={checkmarkOutline} />
                </button>
                <button
                  type="button"
                  className="pipeline-detail-title-btn pipeline-detail-title-cancel"
                  onClick={handleCancelRename}
                  disabled={savingName}
                  aria-label="Cancel"
                  title="Cancel (Esc)"
                >
                  <IonIcon icon={closeOutline} />
                </button>
              </div>
            ) : (
              <>
                <h1 className="pipeline-detail-title" title={workflowType !== (pipeline.displayName || workflowType) ? `Slug: ${workflowType}` : undefined}>
                  {pipeline.displayName || workflowType}
                </h1>
                <button
                  type="button"
                  className="pipeline-detail-title-edit-btn"
                  onClick={handleStartRename}
                  aria-label="Rename agent"
                  title="Rename"
                >
                  <IonIcon icon={createOutline} />
                </button>
                {/* Status / version / last-deployed sit next to the title
                    on the left side. Right side of the header is reserved
                    for actions (Update pill, Trigger button). */}
                <span className="pipeline-detail-meta-sep">·</span>
                <PipelineStatus
                  bundleStatus={pipeline.bundleStatus}
                  warmEnabled={pipeline.warmEnabled}
                  warmProvisioning={warmProvisioning}
                  variant="header"
                />
                <span className="pipeline-detail-meta-sep">·</span>
                <span className="pipeline-detail-version">v{pipeline.version || 0}</span>
                <span className="pipeline-detail-meta-sep">·</span>
                <span className="pipeline-detail-time">
                  Last deployed {pipeline.updatedAt ? timeAgo(pipeline.updatedAt) : '—'}
                </span>
              </>
            )}
          </div>
          <div className="pipeline-detail-actions">
            {/* Marketplace "Update available" pill — App-Store style.
                Only renders when the backend overlay flags this workflow
                as out-of-date relative to the marketplace listing. Sits
                LEFT of Trigger so the primary action is the rightmost
                element (Fitts' law: corner is easiest to hit). */}
            {pipeline.marketplace?.updateAvailable && (
              <button
                type="button"
                className="pipeline-detail-update-pill"
                onClick={() => setUpdateModalOpen(true)}
                title={`Update available — ${pipeline.marketplace.installedVersion || '?'} → ${pipeline.marketplace.currentVersion || '?'}`}
              >
                <IonIcon icon={sparklesOutline} aria-hidden="true" />
                Update available
                {pipeline.marketplace.currentVersion && (
                  <span className="pipeline-detail-update-pill-ver">
                    v{pipeline.marketplace.currentVersion}
                  </span>
                )}
              </button>
            )}
            <PillButton
              onClick={() => setTriggerModalOpen(true)}
              disabled={pipeline.bundleStatus && pipeline.bundleStatus !== 'ready'}
              title={`Trigger ${pipeline.displayName || workflowType}`}
            >
              Trigger
            </PillButton>
          </div>
        </div>

        <TriggerModal
          isOpen={triggerModalOpen}
          onClose={() => setTriggerModalOpen(false)}
          pipeline={pipeline}
          projectId={projectId}
          workflowType={workflowType}
          onTriggered={handleTriggered}
        />

        <AgentModelModal
          isOpen={agentModalOpen}
          onClose={() => setAgentModalOpen(false)}
          variant="pipeline"
          agentOptions={agentOptions}
          seedValue={findAgentOptionValue(
            { aiAgent: pipeline?.aiAgent, aiModel: pipeline?.aiModel },
            agentOptions,
          )}
          seedKey={pipeline?.aiApiKey || ''}
          seedOAuth={pipeline?.aiOAuthToken || ''}
          onSave={(body) => api.patch(`/workflows/${pipeline.uuid}`, body)}
          onSaved={() => fetchPipeline({ silent: true })}
          setToast={setToast}
        />

        {updateModalOpen && pipeline.marketplace && (
          <div
            className="pipeline-update-overlay"
            onClick={() => !updating && setUpdateModalOpen(false)}
            role="presentation"
          >
            <div
              className="pipeline-update-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pipeline-update-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pipeline-update-modal-head">
                <h2 id="pipeline-update-title" className="pipeline-update-modal-title">
                  Update {pipeline.displayName || pipeline.workflowType}
                </h2>
                <button
                  type="button"
                  className="pipeline-update-modal-close"
                  aria-label="Close"
                  onClick={() => !updating && setUpdateModalOpen(false)}
                  disabled={updating}
                >
                  <IonIcon icon={closeOutline} />
                </button>
              </div>
              <div className="pipeline-update-modal-body">
                <p className="pipeline-update-version-line">
                  <span className="pipeline-update-version-from">
                    v{pipeline.marketplace.installedVersion || '?'}
                  </span>
                  <span className="pipeline-update-version-arrow">→</span>
                  <span className="pipeline-update-version-to">
                    v{pipeline.marketplace.currentVersion || '?'}
                  </span>
                </p>
                {pipeline.marketplace.releaseNotes ? (
                  <div className="pipeline-update-notes">
                    {/* Render as plain text for v1; markdown rendering
                        is a follow-up. Whitespace is preserved so
                        authors' bullet lists still read OK. */}
                    <pre className="pipeline-update-notes-pre">
                      {pipeline.marketplace.releaseNotes}
                    </pre>
                  </div>
                ) : (
                  <p className="pipeline-update-no-notes">
                    No release notes provided by the author.
                  </p>
                )}
                <p className="pipeline-update-preserved">
                  Your environment variables, schedule, and custom
                  agent name will be preserved.
                </p>
              </div>
              <div className="pipeline-update-modal-actions">
                <button
                  type="button"
                  className="pipeline-update-btn pipeline-update-btn--ghost"
                  onClick={() => setUpdateModalOpen(false)}
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pipeline-update-btn pipeline-update-btn--primary"
                  onClick={handleUpdate}
                  disabled={updating}
                >
                  <IonIcon icon={cloudUploadOutline} aria-hidden="true" />
                  {updating ? 'Updating…' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab strip */}
        <div className="pipeline-detail-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`pipeline-detail-tab ${activeTab === tab.id ? 'pipeline-detail-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab body */}
        <div className="pipeline-detail-body">
          {activeTab === 'overview' && (
            <OverviewTab
              pipeline={pipeline}
              onCopy={copyToClipboard}
              setToast={setToast}
              onPipelineUpdate={setPipeline}
              onEditAgent={() => setAgentModalOpen(true)}
              projectId={projectId}
              workflowType={workflowType}
            />
          )}
          {activeTab === 'activity' && (
            <ActivityTab
              projectId={projectId}
              workflowType={workflowType}
              executionId={executionId}
              pipeline={pipeline}
              setToast={setToast}
              navigate={navigate}
            />
          )}
          {activeTab === 'logs' && (
            <WorkflowLogsTab
              projectId={projectId}
              workflowType={workflowType}
              status={pipeline.bundleStatus}
            />
          )}
          {activeTab === 'settings' && (
            <SettingsTab
              pipeline={pipeline}
              onDelete={handleDelete}
              setToast={setToast}
              projectId={projectId}
              workflowType={workflowType}
              onChange={() => fetchPipeline({ silent: true })}
            />
          )}
          {activeTab === 'env' && (
            <EnvTab uuid={pipeline.uuid} setToast={setToast} />
          )}
        </div>
      </div>
    </>
  );
};

// ─── Overview ────────────────────────────────────────────────────────

// AuthCard is now the shared atomic in components/shared/AuthCard — imported
// at the top of this file and reused by the managed-app access-control panel.

// Loose IPv4 / IPv6 / CIDR check — gates the IP-allowlist Save button so an
// empty or obviously-malformed line can't be submitted. The backend remains
// the authoritative validator; this is just UI affordance.
const isValidIpOrCidr = (value) => {
  const s = (value || '').trim();
  if (!s) return false;
  const parts = s.split('/');
  if (parts.length > 2) return false;
  const [addr, prefix] = parts;
  if (prefix !== undefined) {
    if (!/^\d{1,3}$/.test(prefix)) return false;
    const max = addr.includes(':') ? 128 : 32;
    if (Number(prefix) > max) return false;
  }
  if (addr.includes(':')) {
    // IPv6 — minimal shape: hex groups separated by colons, at least one "::"
    // or two colons. Not a full RFC parse, just enough to reject junk.
    return /^[0-9a-fA-F:]+$/.test(addr) && (addr.match(/:/g) || []).length >= 2;
  }
  const octets = addr.split('.');
  return octets.length === 4 && octets.every((o) => /^\d{1,3}$/.test(o) && Number(o) <= 255);
};

const OverviewTab = ({ pipeline, onCopy, setToast, onPipelineUpdate, onEditAgent, projectId, workflowType }) => {
  const triggerCmd = `zibby workflow trigger ${pipeline.uuid}`;
  // Schedule trigger commands — set/get/clear, identical to the CLI surface.
  // The set cron is just an illustrative weekday-9am default; user copies and
  // edits to taste. Cron format is standard Unix 5-field; backend converts to
  // EventBridge form so users never need to know AWS's 6-field flavor.
  const scheduleSetCmd   = `zibby workflow schedule ${pipeline.uuid} set "0 9 * * 1-5"`;
  const scheduleShowCmd  = `zibby workflow schedule ${pipeline.uuid}`;
  const scheduleClearCmd = `zibby workflow schedule ${pipeline.uuid} clear`;

  // Customer-facing webhook URL is the per-workflow subdomain
  // (`<slug>-<hash>.workflows.zibby.app/trigger`), NOT the main API path.
  // The subdomain is computed in workflow-service.computeSubdomain and
  // stored on the row at deploy time. Domain suffix depends on the stage
  // the CURRENT frontend is talking to — derive it from the API base
  // host so dev / staging / prod all point at the right tier:
  //   api-prod.zibby.app           → workflows.zibby.app
  //   api-staging.zibby.app        → workflows-staging.zibby.app
  //   api-dev.zibby.app | local    → workflows-dev.zibby.app
  // If the workflow doesn't have a subdomain on its row (shouldn't
  // happen post-deploy, but legacy / partial-save rows might), the
  // section is hidden — no URL is better than a wrong URL.
  const apiBase = process.env.REACT_APP_API_BASE_URL || 'https://api-prod.zibby.app';
  const apiHost = (() => { try { return new URL(apiBase).hostname; } catch { return ''; } })();
  // Local dev has no `*.workflows-dev.zibby.app` subdomain to hit, so the
  // public per-workflow URL is meaningless on localhost. When talking to a
  // local API, surface the direct project-scoped trigger route the local
  // Express server actually serves (server.js:
  // `POST /projects/:projectId/workflows/:type/trigger`) so the shown URL is
  // copy-pasteable and works. dev/staging/prod keep the public subdomain URL.
  const isLocal = apiHost === 'localhost' || apiHost === '127.0.0.1';
  const workflowsDomain = apiHost.startsWith('api-staging.')
    ? 'workflows-staging.zibby.app'
    : apiHost.startsWith('api-prod.')
      ? 'workflows.zibby.app'
      : 'workflows-dev.zibby.app';
  const subdomain  = pipeline.subdomain || null;
  const webhookUrl = isLocal
    ? `${apiBase.replace(/\/$/, '')}/projects/${projectId}/workflows/${workflowType}/trigger`
    : subdomain ? `https://${subdomain}.${workflowsDomain}/trigger` : null;
  const curlExample = webhookUrl
    ? `curl -X POST '${webhookUrl}' \\\n` +
      `  -H 'Authorization: Bearer YOUR_API_TOKEN' \\\n` +
      `  -H 'Content-Type: application/json' \\\n` +
      `  -d '{}'`
    : null;

  // Webhook (HTTP POST) trigger is OPT-IN: live only when
  // `triggers.api === true`. Missing/false ⇒ disabled, and a call to the
  // endpoint 404s server-side. The toggle PATCHes the workflow's triggers
  // and reflects the result locally so the section flips immediately.
  const webhookApiEnabled = pipeline.triggers?.api === true;
  const [togglingWebhook, setTogglingWebhook] = useState(false);
  // Optimistic override so the toggle flips INSTANTLY (no spinner → no button
  // height jump that pushes the section down). null ⇒ follow the prop; once the
  // patched pipeline lands we reset to null and the prop drives it again.
  const [optimisticWebhook, setOptimisticWebhook] = useState(null);
  const webhookEnabled = optimisticWebhook === null ? webhookApiEnabled : optimisticWebhook;

  const handleToggleWebhook = async () => {
    if (togglingWebhook) return;
    const nextValue = !webhookEnabled;
    setOptimisticWebhook(nextValue);   // flip UI now — optimistic, no spinner
    setTogglingWebhook(true);
    try {
      // Backend merges this into the existing triggers map and returns the
      // updated workflow row; lift it straight into page state so every
      // overview field stays in sync (not just `triggers`).
      // PATCH by UUID (`/workflows/{uuid}`) — that's the ONLY registered
      // PATCH route (prod CDK + local server.js); the project-scoped
      // `/projects/{id}/workflows/{type}` resource only exposes GET/PUT, so a
      // PATCH there 404s (this is why Enable silently failed locally).
      const updated = await api.patch(
        `/workflows/${pipeline.uuid}`,
        { triggers: { api: nextValue } },
      );
      // Merge ONLY `triggers` — the PATCH response is the raw DDB row and lacks
      // the GET-computed fields (subdomain → webhookUrl, deployedVersion,
      // updateAvailable). Replacing the whole pipeline with it would blank the
      // webhook section, drop the version to v0, and hide the update badge.
      onPipelineUpdate?.((prev) => {
        if (!prev) return prev;
        const nextTriggers = (updated && typeof updated === 'object' && updated.triggers)
          ? updated.triggers
          : { ...(prev.triggers || {}), api: nextValue };
        return { ...prev, triggers: nextTriggers };
      });
      setOptimisticWebhook(null);   // prop now reflects it — drop the override
    } catch (err) {
      setOptimisticWebhook(null);   // roll back to the prop value
      setToast?.({
        show: true,
        message: `Could not ${nextValue ? 'enable' : 'disable'} webhook: ${err.message || 'unknown error'}`,
        color: 'danger',
      });
    } finally {
      setTogglingWebhook(false);
    }
  };

  return (
    <div className="pipeline-tab pipeline-overview">
      <div className="pipeline-stat-grid">
        {/* `Type` removed — it's just the page title duplicated. The
            workflowType slug is shown in the breadcrumb / heading. */}
        <Stat label="UUID"             value={pipeline.uuid || '—'}        copyable onCopy={onCopy} />
        <Stat label="Version"          value={`v${pipeline.version || 0}`} />
        {pipeline.marketplace?.installedVersion && (
          <Stat
            label="Template"
            value={`${pipeline.marketplace.slug}@v${pipeline.marketplace.installedVersion}`}
          />
        )}
        <AgentStat
          agent={pipeline.aiAgent || ''}
          model={pipeline.aiModel || ''}
          onEdit={onEditAgent}
        />
        <Stat label="Last update"      value={pipeline.updatedAt ? new Date(pipeline.updatedAt).toLocaleString() : '—'} />
      </div>

      <div className="pipeline-section">
        <h3 className="pipeline-section-title">
          Trigger via CLI
          <HelpIcon hint="Trigger a run from your terminal. The UUID is baked into the command — copy and paste." />
        </h3>
        <CmdRow command={triggerCmd} onCopy={onCopy} />
      </div>

      {/* Live status of the currently-attached schedule. Sits between
          "Trigger via CLI" (manual one-off) and "Trigger via schedule"
          (the helper commands to set/change/clear) so the user reads:
          "Manual trigger → What's running automatically → How to change it". */}
      {pipeline.uuid && <ScheduleStatusPanel workflowUuid={pipeline.uuid} />}

      {/* TRIGGER VIA SCHEDULE — cron-based recurring runs.
          Identical CLI flavor to "Trigger via CLI" above so users get the
          same copy-paste UX. The example uses `0 9 * * 1-5` (9am Mon-Fri UTC);
          users edit to taste. Backend (workflows-schedule.js) converts the
          Unix 5-field cron to EventBridge's 6-field form internally. */}
      <div className="pipeline-section">
        <h3 className="pipeline-section-title">
          Trigger via schedule
          <HelpIcon hint="Run on a cron schedule. Standard Unix 5-field: minute hour day-of-month month day-of-week. Add --tz America/Los_Angeles for a timezone, or -p key=value for fixed input params." />
        </h3>
        <CmdRow command={scheduleSetCmd}   onCopy={onCopy} />
        <CmdRow command={scheduleShowCmd}  onCopy={onCopy} />
        <CmdRow command={scheduleClearCmd} onCopy={onCopy} />
      </div>

      {/* TRIGGER VIA WEBHOOK — HTTP POST variant. OPT-IN: live only when
          `triggers.api === true`; missing/false ⇒ the endpoint 404s. The
          section is always shown for deployed workflows (those with a
          `subdomain`) so users can enable it from here. Hidden only when
          there's no subdomain yet (not deployed / partial save) — no URL is
          better than a wrong URL. */}
      {webhookUrl && (
        <div className="pipeline-section pipeline-section--separated">
          {/* Header row: title + state badge on the left, the enable/disable
              action button right-aligned (mirrors the page-header Trigger
              button). The button used to sit at the bottom under the URL. */}
          <div className="pipeline-webhook-header">
            <h3 className="pipeline-section-title pipeline-webhook-header-title">
              Trigger via webhook
              <HelpIcon hint="POST to this endpoint with your API token to trigger a run from CI / CD or any other system. Disabled by default — enable it here. See docs.zibby.app/cloud/triggering for full reference." />
            </h3>
            {/* Same IonToggle the auth cards use — the switch position IS the
                state, so no separate Enabled/Disabled badge. Optimistic flip. */}
            <IonToggle
              className="pipeline-auth-switch"
              checked={webhookEnabled}
              disabled={togglingWebhook}
              onIonChange={(e) => {
                if (e.detail.checked !== webhookEnabled) handleToggleWebhook();
              }}
              aria-label={webhookEnabled ? 'Disable webhook' : 'Enable webhook'}
            />
          </div>

          {webhookEnabled ? (
            <>
              <p className="pipeline-section-note">
                POST to this endpoint with your API token to trigger a run.
              </p>
              <CmdRow command={webhookUrl}  onCopy={onCopy} />
              <CmdRow command={curlExample} onCopy={onCopy} />
            </>
          ) : (
            <p className="pipeline-section-note pipeline-webhook-disabled-note">
              Webhook disabled — calls to this endpoint return 404. Enable it
              to accept HTTP triggers.
            </p>
          )}

          {/* Webhook AUTHENTICATION — only meaningful while the webhook is
              live. Three independently-toggleable methods (basic / IP / token),
              each PATCHing triggers.webhook.<method> and lifting the updated
              workflow row back into page state. */}
          {webhookEnabled && (
            <WebhookAuthConfig
              pipeline={pipeline}
              onPipelineUpdate={onPipelineUpdate}
              onCopy={onCopy}
              setToast={setToast}
            />
          )}
        </div>
      )}
    </div>
  );
};

/**
 * WebhookAuthConfig — three independent webhook auth methods that layer on
 * top of the always-required Bearer API token. Each method owns its own
 * draft state + save spinner and PATCHes only its own sub-object so the
 * backend merge stays narrow:
 *
 *   triggers.webhook.basic = { enabled, username, password? }  (password
 *       plaintext ONLY when the user typed a new one; omitted otherwise so
 *       an enable/disable toggle or username edit never clobbers the hash)
 *   triggers.webhook.ip    = { enabled, allowList: string[] }
 *   triggers.webhook.token = { enabled, generate?: true }
 *
 * GET never returns secrets — basic.password / token raw are write-only.
 * The token PATCH response carries `webhookToken` ONCE; we surface it in a
 * copyable field with a "shown once" note, then fall back to the masked
 * `token.tokenMasked` on every subsequent render.
 */
// Sentinel that renders as dots in the password field when a password is
// already on file (the server never sends the real one back). Cleared on focus;
// treated as "keep existing" on save, never sent.
const PW_SENTINEL = '••••••••';

const WebhookAuthConfig = ({ pipeline, onPipelineUpdate, onCopy, setToast }) => {
  const webhookCfg = pipeline.triggers?.webhook || {};
  const basicCfg = webhookCfg.basic || {};
  const ipCfg = webhookCfg.ip || {};
  const tokenCfg = webhookCfg.token || {};

  // ── Basic auth ──────────────────────────────────────────────────────
  const [basicEnabled, setBasicEnabled] = useState(!!basicCfg.enabled);
  const [basicUsername, setBasicUsername] = useState(basicCfg.username || '');
  // The server never returns the password (hashed). When one is on file we
  // pre-fill this sentinel so the field RENDERS as filled (dots); it's cleared
  // on focus for clean editing and treated as "keep existing" on save.
  const [basicPassword, setBasicPassword] = useState(basicCfg.enabled ? PW_SENTINEL : '');
  const [savingBasic, setSavingBasic] = useState(false);
  // A password is "already set" if the server says basic is enabled — the
  // hash exists server-side even though GET never returns it.
  const passwordOnFile = !!basicCfg.enabled;

  // ── IP allowlist ────────────────────────────────────────────────────
  const [ipEnabled, setIpEnabled] = useState(!!ipCfg.enabled);
  const [ipText, setIpText] = useState((ipCfg.allowList || []).join('\n'));
  const [savingIp, setSavingIp] = useState(false);

  // ── Token ───────────────────────────────────────────────────────────
  const [tokenEnabled, setTokenEnabled] = useState(!!tokenCfg.enabled);
  const [savingToken, setSavingToken] = useState(false);
  // The plaintext token, shown EXACTLY once right after (re)generation.
  // Never persisted, never re-fetched — only the masked value survives a
  // reload (it comes off the workflow row via tokenCfg.tokenMasked).
  const [plaintextToken, setPlaintextToken] = useState(null);

  // Shared PATCH helper — sends only the webhook sub-object passed in,
  // lifts the updated workflow row into page state, and returns the raw
  // response so the token flow can read `webhookToken` off it.
  const patchWebhook = async (webhookPatch) => {
    // PATCH by UUID — same reason as handleToggleWebhook: the project-scoped
    // workflow resource has no PATCH method, only `/workflows/{uuid}` does.
    const updated = await api.patch(
      `/workflows/${pipeline.uuid}`,
      { triggers: { webhook: webhookPatch } },
    );
    // Merge ONLY `triggers` — the PATCH response is the raw DDB row without the
    // GET-computed fields (subdomain/webhookUrl, deployedVersion, updateAvailable).
    // Replacing the whole pipeline would blank the webhook section + reset the
    // version. See handleToggleWebhook for the same fix.
    if (updated && typeof updated === 'object' && updated.triggers) {
      onPipelineUpdate?.((prev) => prev ? { ...prev, triggers: updated.triggers } : prev);
    }
    return updated;
  };

  const handleSaveBasic = async () => {
    if (savingBasic) return;
    if (basicEnabled && !basicUsername.trim()) {
      setToast?.({ show: true, message: 'Username is required for basic auth', color: 'danger' });
      return;
    }
    // Require a password when enabling for the first time (none on file).
    if (basicEnabled && !passwordOnFile && !basicPassword) {
      setToast?.({ show: true, message: 'Set a password to enable basic auth', color: 'danger' });
      return;
    }
    setSavingBasic(true);
    try {
      const basic = { enabled: basicEnabled, username: basicUsername.trim() };
      // Only send a password when the user actually typed one — an untouched
      // sentinel (or blank) tells the backend to keep the existing hash.
      if (basicPassword && basicPassword !== PW_SENTINEL) basic.password = basicPassword;
      await patchWebhook({ basic });
      // Never keep plaintext around. If basic is still enabled a password is now
      // on file → show the field as filled again; otherwise clear it.
      setBasicPassword(basicEnabled ? PW_SENTINEL : '');
      setToast?.({ show: true, message: 'Basic auth saved', color: 'success' });
    } catch (err) {
      setToast?.({ show: true, message: `Could not save basic auth: ${err.message || 'unknown error'}`, color: 'danger' });
    } finally {
      setSavingBasic(false);
    }
  };

  const handleSaveIp = async () => {
    if (savingIp) return;
    // Split on newlines/commas, trim, drop empties.
    const allowList = ipText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (ipEnabled && allowList.length === 0) {
      setToast?.({ show: true, message: 'Add at least one IP or CIDR to enable the allowlist', color: 'danger' });
      return;
    }
    setSavingIp(true);
    try {
      await patchWebhook({ ip: { enabled: ipEnabled, allowList } });
      // Reflect the normalized list back into the textarea.
      setIpText(allowList.join('\n'));
      setToast?.({ show: true, message: 'IP allowlist saved', color: 'success' });
    } catch (err) {
      setToast?.({ show: true, message: `Could not save IP allowlist: ${err.message || 'unknown error'}`, color: 'danger' });
    } finally {
      setSavingIp(false);
    }
  };

  // Top-right Enable/Disable for the Basic card. Enabling just expands the
  // card so the user can type a username + password (committed via Save —
  // basic auth can't be enabled server-side without credentials). Disabling
  // PATCHes the enabled flag off immediately; the config stays on file so
  // re-enabling later doesn't force a re-entry.
  const handleToggleBasic = async (nextEnabled) => {
    if (nextEnabled) {
      setBasicEnabled(true);   // expand only — Save commits the credentials
      return;
    }
    // Optimistic collapse: hide the card body immediately so the Save button
    // never flashes a spinner mid-toggle (no shared-loading flicker). Roll the
    // card back open if the PATCH fails.
    setBasicEnabled(false);
    setBasicPassword('');
    try {
      await patchWebhook({ basic: { enabled: false } });
    } catch (err) {
      setBasicEnabled(true);
      setToast?.({ show: true, message: `Could not disable basic auth: ${err.message || 'unknown error'}`, color: 'danger' });
    }
  };

  // Top-right Enable/Disable for the IP allowlist card. Same shape as Basic:
  // enabling expands (the allowlist is committed via Save — an empty list
  // can't be enabled), disabling PATCHes the flag off immediately.
  const handleToggleIp = async (nextEnabled) => {
    if (nextEnabled) {
      setIpEnabled(true);   // expand only — Save commits the allowlist
      return;
    }
    // Optimistic collapse — same rationale as Basic above.
    setIpEnabled(false);
    try {
      await patchWebhook({ ip: { enabled: false } });
    } catch (err) {
      setIpEnabled(true);
      setToast?.({ show: true, message: `Could not disable IP allowlist: ${err.message || 'unknown error'}`, color: 'danger' });
    }
  };

  // Toggle token on/off WITHOUT regenerating. Used by the on/off switch.
  const handleToggleToken = async (nextEnabled) => {
    // Optimistic flip — update the card immediately, reconcile in the
    // background, roll back on failure. Keeps the toggle from flashing the
    // Generate/Regenerate spinner mid-flight.
    const prev = tokenEnabled;
    setTokenEnabled(nextEnabled);
    // Turning the method off invalidates any one-time token still on screen.
    if (!nextEnabled) setPlaintextToken(null);
    try {
      await patchWebhook({ token: { enabled: nextEnabled } });
    } catch (err) {
      setTokenEnabled(prev);
      setToast?.({ show: true, message: `Could not update token auth: ${err.message || 'unknown error'}`, color: 'danger' });
    }
  };

  // (Re)generate — PATCH with generate:true; the response carries the raw
  // `webhookToken` exactly once. Show it; afterwards only the mask remains.
  const handleGenerateToken = async () => {
    if (savingToken) return;
    setSavingToken(true);
    try {
      const updated = await patchWebhook({ token: { enabled: true, generate: true } });
      setTokenEnabled(true);
      const raw = updated?.webhookToken || null;
      setPlaintextToken(raw);
      setToast?.({
        show: true,
        message: raw ? 'Token generated — copy it now' : 'Token generated',
        color: 'success',
      });
    } catch (err) {
      setToast?.({ show: true, message: `Could not generate token: ${err.message || 'unknown error'}`, color: 'danger' });
    } finally {
      setSavingToken(false);
    }
  };

  // Save-button gating — don't allow submitting an incomplete config. Basic
  // needs a username + (a freshly-typed password, or one already on file). IP
  // needs at least one well-formed IP/CIDR line.
  const basicSaveDisabled =
    !basicUsername.trim() || (!passwordOnFile && !basicPassword);
  const ipLines = ipText.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
  const ipSaveDisabled = ipLines.length === 0 || !ipLines.every(isValidIpOrCidr);

  return (
    <div className="pipeline-webhook-auth">
      <div className="pipeline-auth-cards">
      {/* ── Basic auth ─────────────────────────────────────────────── */}
      <AuthCard
        name="Basic auth"
        description="Require an HTTP Basic username + password."
        hint="Basic or Bearer — either one passes. Both on ≠ both required."
        enabled={basicEnabled}
        onToggle={handleToggleBasic}
        toggling={savingBasic}
      >
        <input
          type="text"
          className="pipeline-env-add-input"
          placeholder="Username"
          value={basicUsername}
          autoComplete="off"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
          data-form-type="other"
          onChange={(e) => setBasicUsername(e.target.value)}
          aria-label="Basic auth username"
        />
        <input
          type="password"
          className="pipeline-env-add-input"
          placeholder="Password"
          value={basicPassword}
          autoComplete="new-password"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
          data-form-type="other"
          onFocus={() => { if (basicPassword === PW_SENTINEL) setBasicPassword(''); }}
          onChange={(e) => setBasicPassword(e.target.value)}
          aria-label="Basic auth password"
        />
        <div className="pipeline-auth-block-actions">
          <PillButton loading={savingBasic} disabled={basicSaveDisabled} onClick={handleSaveBasic}>
            Save
          </PillButton>
        </div>
      </AuthCard>

      {/* ── IP allowlist ───────────────────────────────────────────── */}
      <AuthCard
        name="IP allowlist"
        description="Only accept webhook calls from these IPs / CIDRs."
        hint="Separate gate (AND) — stacks on top of any credential check."
        enabled={ipEnabled}
        onToggle={handleToggleIp}
        toggling={savingIp}
      >
        <textarea
          className="pipeline-env-add-textarea"
          rows={4}
          placeholder={'One IP or CIDR per line, e.g.\n203.0.113.7\n198.51.100.0/24\n2001:db8::/32'}
          value={ipText}
          onChange={(e) => setIpText(e.target.value)}
          aria-label="Allowed IPs and CIDR ranges"
        />
        <div className="pipeline-auth-block-actions">
          <PillButton loading={savingIp} disabled={ipSaveDisabled} onClick={handleSaveIp}>
            Save
          </PillButton>
        </div>
      </AuthCard>

      {/* ── Token ──────────────────────────────────────────────────── */}
      <AuthCard
        name="Bearer token"
        description="Require a bearer token (Authorization: Bearer or X-Webhook-Token)."
        hint="Basic or Bearer — either one passes. Both on ≠ both required."
        enabled={tokenEnabled}
        onToggle={handleToggleToken}
        toggling={savingToken}
      >
        {plaintextToken ? (
          <>
            <p className="pipeline-auth-hint pipeline-auth-token-warn">
              Shown once — copy it now. You won't be able to see it again.
            </p>
            <CmdRow command={plaintextToken} onCopy={onCopy} />
          </>
        ) : tokenCfg.tokenMasked ? (
          <div className="pipeline-auth-token-masked">
            <code className="pipeline-auth-token-masked-code">{tokenCfg.tokenMasked}</code>
            <PillButton loading={savingToken} onClick={handleGenerateToken}>
              Regenerate
            </PillButton>
          </div>
        ) : (
          <p className="pipeline-auth-hint">
            No token yet — generate one to require it on every request.
          </p>
        )}
        {!tokenCfg.tokenMasked && (
          <div className="pipeline-auth-block-actions">
            <PillButton loading={savingToken} onClick={handleGenerateToken}>
              {plaintextToken ? 'Regenerate' : 'Generate'}
            </PillButton>
          </div>
        )}
      </AuthCard>
      </div>
    </div>
  );
};

const Stat = ({ label, value, copyable, onCopy }) => (
  <div className="pipeline-stat">
    <div className="pipeline-stat-label">{label}</div>
    <div className="pipeline-stat-value">
      <span className="pipeline-stat-value-text">{value}</span>
      {copyable && value && value !== '—' && (
        <button
          type="button"
          className="pipeline-stat-copy"
          onClick={() => onCopy(value)}
          title="Copy"
        >
          <IonIcon icon={copyOutline} />
        </button>
      )}
    </div>
  </div>
);

/**
 * Per-workflow AI-agent + model. Editable: clicking opens the shared
 * AgentModelModal, which changes THIS pipeline's agent + model + (per-vendor)
 * API key. Each
 * pipeline owns an independent snapshot of the AI config (taken from the
 * project at create), so the old "picking Codex on a Claude-only project
 * silently fails" footgun is gone — the modal collects that vendor's key
 * right there and stores it encrypted on the workflow row.
 *
 * Resolves the workflow's own override first; falls back to the project
 * default label when both aiAgent + aiModel are absent.
 */
const AgentStat = ({ agent, model, onEdit }) => {
  const currentValue = encodeAgentOption({ aiAgent: agent, aiModel: model });
  const matched = AGENT_OPTIONS.find((opt) => opt.value === currentValue);
  const label = matched?.label || (agent ? `${agent}${model ? ` · ${model}` : ''}` : 'Project default');

  return (
    <div className="pipeline-stat">
      <div className="pipeline-stat-label">Model</div>
      <div className="pipeline-stat-value">
        <button
          type="button"
          className="pipeline-stat-agent-edit"
          onClick={onEdit}
          title="Change model, engine & key"
        >
          <span className="pipeline-stat-value-text">{label}</span>
          <IonIcon icon={createOutline} />
        </button>
      </div>
    </div>
  );
};

const CmdRow = ({ command, onCopy }) => {
  // Whole row is clickable — clicking anywhere on the row copies the
  // command (not just the small icon at the end). The dedicated icon
  // button stays for affordance + keyboard reachability, but
  // stopPropagation prevents the click from double-firing onCopy.
  return (
    <div
      className="pipeline-cmd-row"
      role="button"
      tabIndex={0}
      onClick={() => onCopy(command)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCopy(command);
        }
      }}
      title="Click to copy"
      style={{ cursor: 'pointer' }}
    >
      <code className="pipeline-cmd">{command}</code>
      <button
        type="button"
        className="pipeline-cmd-copy"
        onClick={(e) => { e.stopPropagation(); onCopy(command); }}
        title="Copy"
      >
        <IonIcon icon={copyOutline} />
      </button>
    </div>
  );
};

// ─── Activity ────────────────────────────────────────────────────────

const ActivityTab = ({ projectId, workflowType, executionId, pipeline, setToast, navigate }) => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pagination state — backend returns { executions, nextKey, hasMore }.
  // We append on subsequent fetches and stop when hasMore goes false.
  const [nextKey, setNextKey] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const fetchMoreRef = useRef(null);   // latest fetchMore — for the observer effect

  // When a specific run is selected via URL (/runs/:executionId), fetch
  // its full detail and render the per-node pipeline inline below the
  // list. Avoids the orphan "separate page" + lets the sidebar /
  // breadcrumb keep the workflow context.
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedRunLoading, setSelectedRunLoading] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!executionId) {
      setSelectedRun(null);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    let cancelled = false;
    const fetchOne = async () => {
      try {
        const data = await api.get(`/executions/${executionId}`);
        if (cancelled) return;
        setSelectedRun(data);
        // Same terminal-status gate as the legacy run page — once done,
        // stop polling. Avoids the infinite-fetch bug.
        if (TERMINAL_STATUSES.has(data?.status) && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (err) {
        if (!cancelled) setToast?.({ show: true, message: `Could not load run: ${err.message}`, color: 'danger' });
      } finally {
        if (!cancelled) setSelectedRunLoading(false);
      }
    };
    setSelectedRunLoading(true);
    fetchOne();
    pollRef.current = setInterval(fetchOne, 1500);
    return () => {
      cancelled = true;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [executionId, setToast]);

  // Page-fetch helper — used for both initial load and infinite-scroll
  // appends. Returns the merged data so we can swap `executions` in one
  // setState (avoids the "two re-renders" flash).
  const fetchPage = useCallback(async (cursor) => {
    const qs = new URLSearchParams({ projectId, limit: '50' });
    if (cursor) qs.set('nextKey', cursor);
    const data = await api.get(`/executions?${qs.toString()}`);
    const list = Array.isArray(data?.executions) ? data.executions
              : Array.isArray(data) ? data
              : [];
    // Server filters by `type` (analysis/test/workflow), not by the
    // workflowType slug — so we filter client-side. Fine at current
    // data volumes; revisit if a single project crosses ~1k runs.
    const filtered = list.filter(
      (e) => e.workflowType === workflowType || e.workflow_type === workflowType,
    );
    return { rows: filtered, nextKey: data?.nextKey || null, hasMore: !!data?.hasMore };
  }, [projectId, workflowType]);

  // Initial load — reset everything when projectId/workflowType changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setExecutions([]);
      setNextKey(null);
      try {
        const { rows, nextKey: nk } = await fetchPage(null);
        if (!cancelled) {
          setExecutions(rows);
          setNextKey(nk);
        }
      } catch (err) {
        if (!cancelled) {
          setToast?.({ show: true, message: `Could not load runs: ${err.message}`, color: 'danger' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchPage, setToast]);

  // Infinite scroll — IntersectionObserver on a sentinel below the
  // table fetches the next page when it scrolls into view. Stops when
  // backend reports no more pages (nextKey === null after a fetch).
  const fetchMore = useCallback(async () => {
    if (!nextKey || loadingMore) return;
    setLoadingMore(true);
    try {
      const { rows, nextKey: nk } = await fetchPage(nextKey);
      setExecutions((prev) => [...prev, ...rows]);
      setNextKey(nk);
    } catch (err) {
      setToast?.({ show: true, message: `Could not load more: ${err.message}`, color: 'danger' });
    } finally {
      setLoadingMore(false);
    }
  }, [nextKey, loadingMore, fetchPage, setToast]);
  fetchMoreRef.current = fetchMore;

  useEffect(() => {
    if (executionId) return undefined;        // inline panel open → no auto-load
    if (!nextKey) return undefined;            // no more pages
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) fetchMoreRef.current?.();
    }, { rootMargin: '120px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [nextKey, executionId]);

  const columns = [
    {
      header: 'STATUS',
      key: 'status',
      render: (e) => <span className={`pipeline-run-status pipeline-run-status-${e.status || 'unknown'}`}>{e.status || '—'}</span>,
    },
    { header: 'EXECUTION ID', key: 'executionId', render: (e) => <code className="pipeline-run-id">{(e.executionId || e.id || '').slice(0, 12)}</code> },
    { header: 'STARTED',      key: 'startedAt',   render: (e) => (e.createdAt || e.startedAt) ? timeAgo(e.createdAt || e.startedAt) : '—' },
    { header: 'DURATION',     key: 'duration',    render: (e) => formatDuration(e) },
    // COST column — populated for executions priced under the
    // credits-billing system (Wave 2). Pre-credits executions or
    // still-running rows render `—`. Render never throws on
    // missing data. Cost can land in any of `costMilliCents`,
    // `cost.milliCents`, or legacy `costUsd` (string).
    { header: 'COST',         key: 'cost',        render: (e) => formatExecutionCost(e) },
  ];

  // Same trick as EnvTab: pass an actions array so DataTable adds the
  // 48px-wide actions <th> at the end. Without it, DURATION becomes
  // last-child and gets `width: 48px; text-align: center` from the
  // generic table CSS.
  // Navigate Activity rows to the new pipeline-scoped run detail page
  // (PipelineRunDetailPage). The legacy `/runs/:id` route is kept intact
  // for the existing test-recipe flow — we just don't use it from here.
  const goToRun = (e) =>
    navigate(`/projects/${projectId}/pipelines/${workflowType}/runs/${e.executionId || e.id}`);

  const actions = [
    {
      label: 'View run',
      icon: documentTextOutline,
      onClick: goToRun,
    },
  ];

  // Steps array for ExecutionPipeline. The activity panel renders the
  // canonical workflow shape (every node, always) and overlays reported
  // status on top — same as AnalysisDetailsPage.
  //
  // Node-list source precedence:
  //   1. selectedRun.workflowNodes  — FROZEN snapshot stamped at run
  //                                   start. This is the canonical
  //                                   "what this run actually was"
  //                                   shape, immune to later edits of
  //                                   the template's graph.mjs. Always
  //                                   use this when present.
  //   2. pipeline.graph.nodes       — LIVE shape from the marketplace
  //                                   row overlay. Only reached for
  //                                   legacy runs that pre-date the
  //                                   executor's workflowNodes stamp.
  //                                   May misrepresent old runs if the
  //                                   template's graph has since
  //                                   changed — best-effort fallback.
  //
  // Reported steps fall into two buckets:
  //   - "node" steps         — name matches a workflow node → overlay status
  //   - "lifecycle" steps    — ECS-task phases (Setup, etc.) that don't
  //                            match any workflow node → prepended ahead
  //                            of workflow nodes so the user sees the
  //                            full execution timeline
  //
  // Status promotion logic at the bottom keeps statuses sane against
  // the overall execution status (terminal-success promotes pendings,
  // failed promotes runnings, etc.).
  const inlineSteps = useMemo(() => {
    if (!selectedRun) return [];
    const reported = selectedRun.steps || [];
    const reportedByName = new Map(reported.map((s) => [s.name, s]));

    // Seed the node list from the run's per-run snapshot (the executor
    // freezes the workflow's node list onto the execution row at run start).
    // The executions GET handler now preferentially returns that stamped
    // snapshot (fixed in executions.js — it used to clobber it with a
    // graphJson-only lookup that's null for bundle templates), so this is the
    // full, run-accurate node list for every workflow. graph.nodes is the
    // fallback for older rows with no stamped snapshot. Fully dynamic — node
    // names come from the workflow's own graph, nothing hardcoded here.
    const graphNodes = Array.isArray(pipeline?.graph?.nodes) ? pipeline.graph.nodes : [];
    const workflowNodes = Array.isArray(selectedRun.workflowNodes) && selectedRun.workflowNodes.length > 0
      ? selectedRun.workflowNodes
      : graphNodes.map((n) => ({ name: n.name || n.id, displayName: n.displayName || n.label }));

    // Build the node-row list. Every workflow node renders — pending
    // until a matching reported step shows up to overlay status / logs.
    const workflowNodeNames = new Set(workflowNodes.map((n) => n.name));
    const nodeRows = workflowNodes.map((node, idx) => {
      const r = reportedByName.get(node.name);
      const base = r ? { ...r } : { name: node.name };
      return {
        ...base,
        displayName: node.displayName ||
          base.displayName ||
          base.name?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) ||
          `Step ${idx + 1}`,
        status: base.status || 'pending',
      };
    });

    // Canonical lifecycle phases that happen BEFORE the workflow's own
    // nodes run. Render them as placeholders from t=0 so the user sees
    // the full timeline immediately ("Setup → fetch_issues → …") rather
    // than Setup popping in on the next poll once the backend stamps
    // it onto execution.steps. Reported status overlays the placeholder
    // when it lands. Currently just Setup (ECS task provisioning +
    // container bootstrap); add 'Bundle Sync' / 'Egress Check' here if
    // future executor stages report under those names.
    //
    // CRITICAL: `name` must match the lowercase name the backend stamps
    // (analyze.js / workflow-executor.js write `name: 'setup'`). A
    // case-mismatched placeholder doesn't dedup against the reported
    // step → renders twice.
    const LIFECYCLE_PHASES = [
      { name: 'setup', displayName: 'Setup' },
    ];
    const lifecycleRows = LIFECYCLE_PHASES.map((phase) => {
      const r = reportedByName.get(phase.name);
      return {
        ...(r || {}),
        name: phase.name,
        displayName: r?.displayName || phase.displayName,
        status: r?.status || 'pending',
      };
    });

    // Reported steps that match neither a workflow node nor a known
    // lifecycle phase (future executor stages we haven't enumerated):
    // append AFTER lifecycle phases, BEFORE workflow nodes, so they
    // still surface visually instead of being silently dropped.
    const knownNames = new Set([
      ...workflowNodeNames,
      ...LIFECYCLE_PHASES.map((p) => p.name),
    ]);
    const orphanLifecycleRows = reported
      .filter((s) => !knownNames.has(s.name))
      .map((s) => ({
        ...s,
        displayName: s.displayName || s.name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        status: s.status || 'pending',
      }));

    const out = [...lifecycleRows, ...orphanLifecycleRows, ...nodeRows];
    if (!out.length) return [];

    const exStatus = selectedRun.status;
    if (['completed', 'success', 'succeeded'].includes(exStatus)) {
      // When the workflow as a whole succeeded, distinguish two cases
      // for any node we never received a progress event for:
      //   1. The node ran but its progress message was dropped/raced —
      //      treat as completed (green ✓). Detection: it appears in the
      //      reported `steps` array, just with `status: 'pending'`.
      //   2. The graph SKIPPED the node via a conditional edge — e.g.
      //      sentry-triage routes `fetch_issues -> END` when 0 issues,
      //      bypassing classify + dispatch_alerts entirely. Detection:
      //      the node is in `workflowNodes` but never made it into
      //      `reported` (we built the row from the snapshot placeholder).
      //      Render as `skipped` (gray) so the operator can tell at a
      //      glance "nothing to do" from "ran clean".
      const reportedNames = new Set(reported.map((s) => s.name));
      out.forEach((s) => {
        if (s.status === 'pending' || s.status === 'queued') {
          s.status = reportedNames.has(s.name) ? 'completed' : 'skipped';
        }
      });
    }
    if (exStatus === 'failed') {
      out.forEach((s) => {
        if (s.status === 'running' || s.status === 'in_progress') s.status = 'failed';
        else if (s.status === 'queued' || s.status === 'pending')  s.status = 'cancelled';
      });
    }
    return out;
  }, [selectedRun, pipeline?.graph?.nodes]);

  return (
    <div className="pipeline-tab">
      {/* Activity list — hidden when an execution is selected so the
          focus is fully on the run detail below. Close button on the
          detail panel brings the list back. */}
      {!executionId && (
        <>
          <DataTable
            data={executions}
            columns={columns}
            loading={loading}
            rowKey="executionId"
            actions={actions}
            onRowClick={goToRun}
            emptyState={{
              title: 'No runs yet',
              description: `Trigger this agent with: zibby workflow trigger <uuid>`,
            }}
          />
          {/* Infinite-scroll sentinel — when this scrolls into view, the
              IntersectionObserver above kicks the next page fetch.
              Renders three states:
                - "Loading more…"  during a page fetch
                - blank            when more pages exist but not yet
                                   loading (sentinel is the trigger)
                - "End of runs"    when nextKey is null (no more pages)
              Always kept in the tree so users see the bottom marker. */}
          {!loading && executions.length > 0 && (
            <div
              ref={sentinelRef}
              className="pipeline-activity-sentinel"
              aria-hidden="true"
            >
              {loadingMore ? 'Loading more…' : (nextKey ? '' : '— End of runs —')}
            </div>
          )}
        </>
      )}

      {/* Inline run detail — shown when a row is selected via URL. The
          standalone PipelineRunDetailPage used to render this on a
          separate route, but the separate page lost the sidebar +
          breadcrumb context. Rendering inline keeps the workflow page
          intact and avoids one more route to maintain. */}
      {executionId && (
        <div className="pipeline-run-inline">
          {/* Run header — restored to match the standalone
              PipelineRunDetailPage's chrome (status pill + execution id
              + copy button + Started <ago>). The "Run <id>" h3 alone was
              too sparse once the ExecutionPipeline below renders
              placeholder steps for executions that haven't reported
              status yet; the meta row gives the user actual context
              (is it running? when did it start?). */}
          <div className="pipeline-run-inline-header">
            <div className="pipeline-run-meta">
              {selectedRun?.status && (
                <>
                  <span className={`pipeline-run-statuspill pipeline-run-statuspill-${selectedRun.status}`}>
                    {selectedRun.status}
                  </span>
                  <span className="pipeline-run-meta-sep">·</span>
                </>
              )}
              <code className="pipeline-run-id">{executionId.slice(0, 12)}</code>
              <button
                type="button"
                className="pipeline-run-copy"
                onClick={() => navigator.clipboard?.writeText(executionId).then(() => setToast?.({ show: true, message: 'Copied execution ID', color: 'success' }))}
                title="Copy execution ID"
              >
                <IonIcon icon={copyOutline} />
              </button>
              {(selectedRun?.createdAt || selectedRun?.startedAt) && (
                <>
                  <span className="pipeline-run-meta-sep">·</span>
                  <span className="pipeline-run-time">
                    Started {timeAgo(selectedRun.createdAt || selectedRun.startedAt)}
                  </span>
                </>
              )}
            </div>
            {selectedRun?.totalUsage && (Number(selectedRun.totalUsage.input_tokens || 0) + Number(selectedRun.totalUsage.output_tokens || 0)) > 0 && (
              <div className="pipeline-run-total-usage">
                <span className="pipeline-run-total-usage-tokens">
                  {formatTokensCompact(Number(selectedRun.totalUsage.input_tokens || 0))} in
                  {' · '}
                  {formatTokensCompact(Number(selectedRun.totalUsage.output_tokens || 0))} out
                  {Number(selectedRun.totalUsage.cache_read_tokens || 0) > 0 && (
                    <>{' · '}{formatTokensCompact(Number(selectedRun.totalUsage.cache_read_tokens))} cached</>
                  )}
                </span>
                {Number(selectedRun.totalUsage.estimated_cost_usd || 0) > 0 && (
                  <span className="pipeline-run-total-usage-cost">
                    {' · $'}{Number(selectedRun.totalUsage.estimated_cost_usd).toFixed(4)}
                  </span>
                )}
              </div>
            )}
          </div>
          {selectedRunLoading && !selectedRun ? (
            // Skeleton rows that mirror the eventual layout: one
            // wide "status banner" placeholder + 3-4 step rows
            // matching workflowNodes length when known. Same shape
            // as the real ExecutionPipeline so the form doesn't
            // jump when the data arrives — replaces the old plain
            // "Loading run…" text which looked broken next to the
            // populated header.
            <div className="pipeline-run-inline-skeleton" aria-busy="true">
              <div className="pipeline-run-inline-skeleton-bar pipeline-run-inline-skeleton-bar--wide" />
              {Array.from({
                length: Math.max(
                  3,
                  (Array.isArray(pipeline?.graph?.nodes) ? pipeline.graph.nodes.length : 0) + 1,
                ),
              }).map((_, i) => (
                <div key={i} className="pipeline-run-inline-skeleton-row">
                  <div className="pipeline-run-inline-skeleton-dot" />
                  <div className="pipeline-run-inline-skeleton-bar pipeline-run-inline-skeleton-bar--narrow" />
                </div>
              ))}
            </div>
          ) : !selectedRun ? (
            <div className="pipeline-run-inline-empty">Run not found.</div>
          ) : (
            <>
              {/* Run-level error banner. For setup-time failures (state
                  validation, missing inputs, etc.) the per-step logs are
                  often a 2-line stub — the actual reason lives on the
                  execution row. Surface it up top so the user can see
                  WHY the run failed without hunting through CloudWatch. */}
              {(selectedRun.status === 'failed' || selectedRun.status === 'cancelled') &&
               (selectedRun.error || selectedRun.failureReason) && (
                <div className="pipeline-run-error-banner">
                  <div className="pipeline-run-error-banner__label">Failure</div>
                  <pre className="pipeline-run-error-banner__msg">{selectedRun.error || selectedRun.failureReason}</pre>
                </div>
              )}
              <ExecutionPipeline steps={inlineSteps} executionStatus={selectedRun.status} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Compact K/M token formatter — mirrors ExecutionPipeline's helper,
 * duplicated here because the run-header total chip lives outside that
 * component. Trim trailing .0/.00 so "12.0K" → "12K" reads cleaner.
 */
function formatTokensCompact(n) {
  if (!n || n <= 0) return '0';
  if (n < 1000) return String(n);
  if (n < 10000)   return (n / 1000).toFixed(2).replace(/\.?0+$/, '') + 'K';
  if (n < 100000)  return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  if (n < 1000000) return Math.round(n / 1000) + 'K';
  return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
}

const formatDuration = (e) => {
  const start = e.createdAt || e.startedAt;
  const end   = e.completedAt || e.endedAt;
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (Number.isNaN(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

/**
 * Format an execution row's cost for the Activity table. The credit-
 * ledger stamps cost on terminal executions; non-terminal rows + pre-
 * credits-era rows return `—` rather than throwing.
 *
 * Cost sources (priority order):
 *   1. e.costMilliCents      — integer mc set by the ledger writer
 *   2. e.cost.milliCents     — nested form some handlers emit
 *   3. e.costUsd             — legacy USD string fallback
 *
 * Numbers < 1¢ format with 4dp ($0.0185); ≥ 1¢ with 4dp also so the
 * row stays aligned in tabular-nums. Trailing zeros are kept so columns
 * don't shift.
 */
const formatExecutionCost = (e) => {
  if (!e) return '—';
  const isTerminal = TERMINAL_STATUSES.has(e.status);
  if (!isTerminal) return '—';
  const mc = Number(
    e.costMilliCents ??
    e.cost?.milliCents ??
    (e.costUsd != null ? Number(e.costUsd) * 100000 : null)
  );
  if (!Number.isFinite(mc) || mc < 0) return '—';
  const usd = mc / 100000;
  // 4dp for tabular alignment — cents-only runs would otherwise show
  // as $0.02 next to $0.0185 and the column would visually shift.
  return `$${usd.toFixed(4)}`;
};

// ─── Settings ────────────────────────────────────────────────────────

const SettingsTab = ({ pipeline, onDelete, setToast, projectId, workflowType, onChange }) => (
  <div className="pipeline-tab">
    <div className="pipeline-section">
      <h3 className="pipeline-section-title">Repository</h3>
      <p className="pipeline-section-hint">
        Sources are uploaded with each <code>zibby workflow deploy</code>. The current bundle
        was built from version <strong>v{pipeline.version || 0}</strong>.
      </p>
    </div>

    <WarmPoolSection
      setToast={setToast}
      projectId={projectId}
      workflowType={workflowType}
      onChange={onChange}
    />

    <DangerZone
      label={workflowType}
      hint="Deleting this agent removes its bundle from S3 and unregisters the UUID. All run history stays. This cannot be undone."
      buttonLabel="Delete agent"
      modalTitle={`Delete agent ${workflowType}?`}
      modalWarning={(
        <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: 14, lineHeight: 1.55, marginTop: 0, marginBottom: 12 }}>
          This removes the bundle from S3 and unregisters the UUID. Run history stays.{' '}
          <strong style={{ color: '#fff' }}>This cannot be undone.</strong>
        </p>
      )}
      confirmLabel="Delete agent"
      busyLabel="Deleting…"
      onConfirm={onDelete}
    />
  </div>
);

/**
 * WarmPoolSection — account-scoped addon control rendered inside the
 * workflow Settings tab. The warm pool is currently an account-level
 * resource (not per-workflow) — same control surfaces on the Billing
 * page; we mirror it here so users discovering the per-workflow cold
 * start can fix it without a second navigation step.
 *
 * Three render states:
 *   loading  → spinner only
 *   inactive → "Off" + N=1..5 enable buttons
 *   active   → quantity + per-min rate + Disable button
 *
 * 402 on enable → InsufficientCreditsModal, parameterised with the
 * requested quantity / required + balance milli-cents from the response
 * body. Other 4xx → toast.
 */
function WarmPoolSection({ setToast, projectId, workflowType, onChange }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'inactive' | 'active' | 'error'
  const [quantity, setQuantity] = useState(0);
  // perInstancePerMinuteMilliCents — fetched from the status response so
  // we don't hard-code the rate in the frontend (the backend's
  // pricing-rates.js is the source of truth and supports per-account
  // overrides; baking the number here would silently desync any VIP
  // discount). Falls back to 58 (the published default = $0.06/hr × ~58)
  // ONLY for the brief moment between mount and first response, so the
  // UI doesn't render "$0/hr" before status resolves.
  const [perInstanceRate, setPerInstanceRate] = useState(58);
  // SSOT for the user-selectable counts. Backend echoes its config on
  // the status response (`pricing.quantityOptions`); we keep the static
  // import as the cold-start fallback so the menu renders before the
  // first fetch lands. Adding/removing a count = one edit in
  // backend/src/config/plans.js → here via API.
  const [quantityOptions, setQuantityOptions] = useState(WARM_POOL_QUANTITY_OPTIONS);
  const [busy, setBusy] = useState(false);
  // Insufficient-credits modal state — opened on a 402 from enable.
  const [credsModal, setCredsModal] = useState({
    open: false, requestedQty: 0, required: null, balance: null,
  });
  // Confirm-before-enable modal state — opened on any "Create N workers"
  // click (main button OR dropdown item). Forces a deliberate confirm
  // since enabling kicks off per-minute billing immediately.
  const [confirmEnable, setConfirmEnable] = useState({ open: false, qty: 0 });
  // Disable confirm — styled modal (NOT a native window.confirm), mirroring
  // the enable flow.
  const [confirmDisable, setConfirmDisable] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      // Per-pipeline status — read THIS pipeline's own warm state (the
      // workflow row), not the caller account's pool. The warm-pool resource
      // now lives on the project-owner account, so the bare account-level
      // status would wrongly show "Off" for a warm pipeline.
      const data = await Addons.getWarmPoolStatus({ projectId, workflowType });
      const rate = Number(data?.pricing?.perInstancePerMinuteMilliCents);
      if (Number.isFinite(rate) && rate > 0) setPerInstanceRate(rate);
      const opts = data?.pricing?.quantityOptions;
      if (Array.isArray(opts) && opts.length > 0) {
        setQuantityOptions(opts.filter((n) => Number.isInteger(n) && n > 0));
      }
      if (data?.enabled) {
        setStatus('active');
        setQuantity(Number(data.quantity) || 0);
      } else {
        setStatus('inactive');
        setQuantity(0);
      }
    } catch (err) {
      console.error('[warm-pool] status fetch failed:', err);
      setStatus('error');
    }
  }, [projectId, workflowType]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const enableWarmPool = async (n) => {
    if (busy) return;
    setBusy(true);
    try {
      // Per-pipeline mode: backend writes BOTH workflow.warmEnabled and
      // account.warmPool atomically. Without the workflow identifiers,
      // billing flips on but no runners actually launch — that's the
      // bug this whole call site is here to avoid.
      await Addons.enableWarmPool(n, { projectId, workflowType });
      Credits.fireChangedEvent();
      setToast?.({ show: true, message: `Warm worker enabled with ${n} runner${n > 1 ? 's' : ''}.`, color: 'success' });
      await fetchStatus();
      // Refetch the pipeline row so the header status pill (which reads
      // workflow.warmEnabled) flips "Sleeping" → "Live".
      if (typeof onChange === 'function') await onChange();
    } catch (err) {
      // 402 → dedicated modal with requested-context. The handler's
      // response body has the shape { error, requiredMilliCents,
      // balanceMilliCents, topUpUrl } — pass through.
      if (err?.status === 402) {
        const body = err.body || {};
        setCredsModal({
          open: true,
          requestedQty: n,
          required: Number.isFinite(Number(body.requiredMilliCents)) ? Number(body.requiredMilliCents) : null,
          balance: Number.isFinite(Number(body.balanceMilliCents)) ? Number(body.balanceMilliCents) : null,
        });
      } else {
        const msg = err?.body?.error || err?.body?.message || err?.message || 'Enable failed';
        setToast?.({ show: true, message: msg, color: 'danger' });
      }
    } finally {
      setBusy(false);
    }
  };

  // Confirmation handled by the styled MktAlertModal (same as enable) — this
  // just performs the disable once the user confirms.
  const disableWarmPool = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await Addons.disableWarmPool({ projectId, workflowType });
      Credits.fireChangedEvent();
      setToast?.({ show: true, message: 'Warm worker disabled for this agent.', color: 'success' });
      await fetchStatus();
      if (typeof onChange === 'function') await onChange();
    } catch (err) {
      const msg = err?.body?.error || err?.body?.message || err?.message || 'Disable failed';
      setToast?.({ show: true, message: msg, color: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  // Per-quantity rate string — actual total, not per-worker × N. So
  // 2 workers = "$0.06/hr", not "$0.03/hr × 2". formatRate takes
  // milli-cents-per-minute and renders as "$X/hr" via the shared helper.
  const rateForQty = (n) => formatRate(perInstanceRate * n);

  // Open the confirm modal instead of calling the API directly. The
  // modal's primary CTA invokes enableWarmPool with the chosen quantity.
  const requestEnable = (n) => setConfirmEnable({ open: true, qty: n });

  return (
    <div className="pipeline-section pipeline-section--separated">
      <h3 className="pipeline-section-title">Warm agent</h3>
      <p className="pipeline-section-hint">
        Keep this agent always-warm — skip the ~60s cold-start by running
        pre-warmed runners. Paid feature — per-minute charges begin when
        enabled and stop when disabled.
      </p>

      {status === 'loading' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
          <IonSpinner name="dots" />
          <span className="pipeline-section-hint" style={{ margin: 0 }}>Loading…</span>
        </div>
      )}

      {status === 'error' && (
        <p className="pipeline-section-hint" style={{ color: 'rgba(248, 113, 113, 0.85)' }}>
          Could not load warm-agent status. <button
            type="button"
            className="warm-pool-link-btn"
            onClick={fetchStatus}
          >Retry</button>
        </p>
      )}

      {status === 'inactive' && (
        <>
          <p className="pipeline-section-hint">
            Current state: <strong>Off</strong> · Rate when enabled: <strong>{formatRate(perInstanceRate)} per worker</strong>
          </p>
          <SplitButton
            primaryLabel={`Create 1 warm worker (${rateForQty(1)})`}
            primaryIcon={flashOutline}
            onPrimary={() => requestEnable(1)}
            disabled={busy}
            size="lg"
            menuDirection="down"
            menuHeader={{ icon: flashOutline, label: 'Warm agent' }}
            menuHelp="Pick how many pre-warmed runners to keep running. Per-minute charges apply to all of them."
            options={quantityOptions.map((n) => ({
              value: n,
              label: `Create ${n} warm worker${n === 1 ? '' : 's'}`,
              hint: rateForQty(n),
            }))}
            onSelect={(n) => requestEnable(n)}
          />
        </>
      )}

      {status === 'active' && (
        <>
          <p className="pipeline-section-hint" style={{ margin: '0 0 4px' }}>
            Current state: <strong>{quantity} warm worker{quantity > 1 ? 's' : ''}</strong>
          </p>
          <p className="pipeline-section-hint" style={{ margin: '0 0 12px' }}>
            Rate: <strong>{rateForQty(quantity)}</strong>
            {' '}· Per-day estimate: <strong>{formatBalance(perInstanceRate * quantity * 60 * 24)}</strong>
          </p>
          <DangerButton
            icon={trashOutline}
            onClick={() => setConfirmDisable(true)}
            disabled={busy}
          >
            Disable warm agent
          </DangerButton>
        </>
      )}

      <InsufficientCreditsModal
        open={credsModal.open}
        onClose={() => setCredsModal((s) => ({ ...s, open: false }))}
        title="Add credits to enable warm pool"
        ariaLabel="Insufficient credits to enable warm pool"
        actionLabel={`enable warm pool with ${credsModal.requestedQty} worker${credsModal.requestedQty > 1 ? 's' : ''}`}
        rateLabel={rateForQty(credsModal.requestedQty || 1)}
        minRuntimeLabel="1 day"
        minRequiredMilliCents={credsModal.required}
        balanceMilliCents={credsModal.balance}
        primaryLabel="Go to Billing"
        primaryTo="/billing"
        secondaryLabel="Cancel"
      />

      {/* Confirm modal — fires before the API call so the user has a
          chance to back out. Reuses the existing MktAlertModal atom that
          AppsMarketplaceSection / billing flow already use. */}
      <MktAlertModal
        open={confirmEnable.open}
        onClose={() => setConfirmEnable({ open: false, qty: 0 })}
        title="Enable warm agent?"
        ariaLabel="Confirm warm agent enable"
        primaryLabel={`Yes, create ${confirmEnable.qty} warm worker${confirmEnable.qty > 1 ? 's' : ''}`}
        onPrimary={() => {
          const n = confirmEnable.qty;
          setConfirmEnable({ open: false, qty: 0 });
          enableWarmPool(n);
        }}
        secondaryLabel="Cancel"
      >
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, marginBottom: 12 }}>
          You're about to enable warm agent with{' '}
          <strong style={{ color: '#fff' }}>
            {confirmEnable.qty} warm worker{confirmEnable.qty > 1 ? 's' : ''}
          </strong>.
        </p>
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, margin: 0 }}>
          Per-minute charges begin immediately at{' '}
          <strong style={{ color: '#fff' }}>{rateForQty(confirmEnable.qty)}</strong>
          {' '}(~{formatBalance(perInstanceRate * (confirmEnable.qty || 1) * 60 * 24)} per day).
          {' '}You can disable any time on this page to stop charges.
        </p>
      </MktAlertModal>

      {/* Disable confirm — styled modal (was a native window.confirm),
          mirroring the enable flow above. */}
      <MktAlertModal
        open={confirmDisable}
        onClose={() => setConfirmDisable(false)}
        title="Disable warm agent?"
        ariaLabel="Confirm warm agent disable"
        primaryLabel="Yes, disable"
        onPrimary={() => { setConfirmDisable(false); disableWarmPool(); }}
        secondaryLabel="Cancel"
      >
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, margin: 0 }}>
          The warm pool resizes (or shuts down if no other warm agents remain)
          and <strong style={{ color: '#fff' }}>per-minute charges stop</strong>.
          Existing runners drain on the next reconcile.
        </p>
      </MktAlertModal>
    </div>
  );
}

// ─── Env ─────────────────────────────────────────────────────────────

const EnvTab = ({ uuid, setToast }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addKey, setAddKey] = useState('');
  const [addValue, setAddValue] = useState('');
  const [adding, setAdding] = useState(false);
  // Per-key reveal state. `revealed[key]` holds the decrypted plaintext once
  // fetched (presence = shown); `revealing[key]` is the in-flight flag.
  const [revealed, setRevealed] = useState({});
  const [revealing, setRevealing] = useState({});

  const fetchKeys = useCallback(async () => {
    if (!uuid) { setLoading(false); return; }
    setLoading(true);
    try {
      // Server returns `{ keys: [...], entries: [{key,lastFour}] }`.
      // `entries` is preferred (carries the masked suffix); fall back to
      // `keys` for older API responses.
      const data = await api.get(`/workflows/${uuid}/env`);
      const list = Array.isArray(data?.entries) ? data.entries
                : Array.isArray(data?.keys)    ? data.keys
                : Array.isArray(data)          ? data
                : [];
      setKeys(list);
    } catch (err) {
      setToast?.({ show: true, message: `Could not load env: ${err.message}`, color: 'danger' });
    } finally {
      setLoading(false);
    }
  }, [uuid, setToast]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  // Reveal / hide a single value. Hide is local (drop the cached plaintext);
  // reveal hits the authorized decrypt endpoint and caches the result.
  const toggleReveal = useCallback(async (key) => {
    if (key in revealed) {
      setRevealed((c) => { const n = { ...c }; delete n[key]; return n; });
      return;
    }
    setRevealing((c) => ({ ...c, [key]: true }));
    try {
      const data = await api.get(`/workflows/${uuid}/env/${encodeURIComponent(key)}/reveal`);
      setRevealed((c) => ({ ...c, [key]: data?.value ?? '' }));
    } catch (err) {
      setToast?.({ show: true, message: `Reveal failed: ${err.message}`, color: 'danger' });
    } finally {
      setRevealing((c) => { const n = { ...c }; delete n[key]; return n; });
    }
  }, [uuid, revealed, setToast]);

  // Submit handler for the Add modal. Server-side validation owns the
  // canonical rules (key shape, length, reserved-prefix list) — the
  // client just gates on "non-empty key" so the user gets a friendly
  // error instead of a 400 on whitespace-only input. Everything else
  // surfaces through the toast on failure.
  const submitAdd = useCallback(async () => {
    const k = addKey.trim();
    const v = addValue;
    if (!k) {
      setToast?.({ show: true, message: 'Key is required', color: 'danger' });
      return;
    }
    setAdding(true);
    try {
      await api.patch(`/workflows/${uuid}/env/${encodeURIComponent(k)}`, { value: v });
      setToast?.({ show: true, message: `Set ${k}`, color: 'success' });
      setAddOpen(false);
      setAddKey('');
      setAddValue('');
      fetchKeys();
    } catch (err) {
      setToast?.({ show: true, message: `Set failed: ${err.message}`, color: 'danger' });
    } finally {
      setAdding(false);
    }
  }, [uuid, addKey, addValue, fetchKeys, setToast]);

  const rows = keys.map((k) => (typeof k === 'string' ? { key: k, lastFour: '' } : k));
  const columns = [
    { header: 'KEY',   key: 'key',   render: (r) => <code className="pipeline-env-key">{r.key}</code> },
    {
      header: 'VALUE',
      key: 'value',
      render: (r) => {
        const isRevealed = r.key in revealed;
        const isRevealing = !!revealing[r.key];
        return (
          <span className="pipeline-env-mask">
            {isRevealed ? (
              <code className="pipeline-env-value-plain">{revealed[r.key] || '(empty)'}</code>
            ) : (
              <>
                <span className="pipeline-env-mask-dots" aria-hidden="true">
                  {'•'.repeat(24)}
                </span>
                {r.lastFour ? <span className="pipeline-env-mask-suffix">{r.lastFour}</span> : null}
              </>
            )}
            <button
              type="button"
              className="pipeline-env-reveal-btn"
              onClick={() => toggleReveal(r.key)}
              disabled={isRevealing}
              title={isRevealed ? 'Hide value' : 'Reveal value'}
              aria-label={isRevealed ? 'Hide value' : 'Reveal value'}
            >
              <IonIcon icon={isRevealed ? eyeOffOutline : eyeOutline} />
            </button>
          </span>
        );
      },
    },
  ];

  // Adding actions for two reasons:
  //   1. Delete is genuinely useful (env API supports DELETE /workflows/{uuid}/env/{key})
  //   2. Without an actions column, DataTable's `th:last-child` CSS rule
  //      (`width: 48px; text-align: center`) hijacks the VALUE column,
  //      squeezing the masked dots into a 48px box and wrapping them.
  const actions = [
    {
      label: 'Delete key',
      icon: trashOutline,
      danger: true,
      onClick: async (row) => {
        if (!window.confirm(`Delete env var "${row.key}"? You'll need to re-set it from the CLI.`)) return;
        try {
          await api.delete(`/workflows/${uuid}/env/${encodeURIComponent(row.key)}`);
          setToast?.({ show: true, message: `Deleted ${row.key}`, color: 'success' });
          fetchKeys();
        } catch (err) {
          setToast?.({ show: true, message: `Delete failed: ${err.message}`, color: 'danger' });
        }
      },
    },
  ];

  return (
    <div className="pipeline-tab">
      <div className="pipeline-env-header">
        <p className="pipeline-section-hint">
          Environment variables for this agent. Values are write-only — also settable via{' '}
          <code>zibby workflow env set {`<KEY>`} {`<value>`} --workflow {uuid}</code>.
        </p>
        {/* Header Add button: only show when there's at least one row.
            The empty-state card has its own centered Add button (the
            canonical "no data → big primary CTA" pattern from the
            Projects page), so rendering the header button too gave us
            doubled affordances overlapping the same skeleton lines
            DataTable still paints under the empty card. */}
        {!loading && rows.length > 0 && (
          <IonButton size="small" onClick={() => setAddOpen(true)}>
            <IonIcon slot="start" icon={addOutline} />
            Add
          </IonButton>
        )}
      </div>
      <DataTable
        data={rows}
        columns={columns}
        loading={loading}
        rowKey="key"
        actions={actions}
        emptyState={{
          title: 'No env vars set',
          description: 'Click Add to set the first one, or use the CLI: zibby workflow env set <KEY> <value>',
          actionLabel: 'Add',
          onAction: () => setAddOpen(true),
        }}
      />
      {addOpen && (
        <div
          className="pipeline-env-add-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pipeline-env-add-title"
          onClick={(e) => { if (e.target === e.currentTarget) setAddOpen(false); }}
        >
          <div className="pipeline-env-add-modal">
            <header className="pipeline-env-add-header">
              <h3 id="pipeline-env-add-title">Add environment variable</h3>
              <button
                type="button"
                className="pipeline-env-add-close"
                aria-label="Close"
                onClick={() => setAddOpen(false)}
              >
                <IonIcon icon={closeOutline} />
              </button>
            </header>
            <div className="pipeline-env-add-body">
              <label className="pipeline-env-add-field">
                <span>Key</span>
                <input
                  type="text"
                  className="pipeline-env-add-input"
                  placeholder="SLACK_CHANNEL"
                  value={addKey}
                  onChange={(e) => setAddKey(e.target.value)}
                  // Letters / digits / underscore is the env-var convention.
                  // Strip invalid chars on input so the user can't accidentally
                  // type lowercase + dash and only learn on submit. The server
                  // also validates, so any drift in the rules surfaces there.
                  onBlur={(e) => setAddKey(e.target.value.trim())}
                  spellCheck={false}
                  autoFocus
                />
              </label>
              <label className="pipeline-env-add-field">
                <span>Value</span>
                <textarea
                  className="pipeline-env-add-textarea"
                  placeholder="#alerts-prod"
                  value={addValue}
                  onChange={(e) => setAddValue(e.target.value)}
                  rows={3}
                  spellCheck={false}
                />
              </label>
              <p className="pipeline-env-add-hint">
                Values are write-only — once saved, the server never returns
                them. To rotate, set the same key again.
              </p>
            </div>
            <footer className="pipeline-env-add-footer">
              <button
                type="button"
                className="pipeline-env-add-cancel"
                onClick={() => setAddOpen(false)}
                disabled={adding}
              >
                Cancel
              </button>
              <IonButton onClick={submitAdd} disabled={adding || !addKey.trim()}>
                {adding ? 'Saving…' : 'Save'}
              </IonButton>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Logs tab ────────────────────────────────────────────────────────
//
// Paginated viewer for the workflow's CONTAINER logs (the `/ecs/zibby-
// agent-<stage>` execution log group — includes the warm-runner "warming
// up" lines as well as per-run output), served by the dedicated
// workflow-logs API (see services/workflowLogs.js). Mirrors the apps'
// LogsTab UX in AppDetailPage.js: a monospace buffer, infinite-scroll to
// load OLDER pages (prepend + restore scroll), and a live-tail poll while
// any run is executing.
//
// Shape differences from the apps LogsTab that drive this implementation:
//   - getWorkflowLogs returns `lines` as objects { timestamp, message,
//     jobId } (NOT pre-formatted "<iso>  <msg>" strings), so we render
//     timestamp + message ourselves.
//   - Paging is a TIME-BOUNDED tail, NOT a forward CloudWatch cursor (that
//     cursor walked the wrong direction — see BUG B). Initial/live fetch
//     omits `before` → the NEWEST window (chronological, newest at bottom).
//     Scroll-up passes `before=<oldest-loaded-ts>` → the page of OLDER
//     events, which we PREPEND. `hasOlder` (boolean) says whether scrolling
//     further up yields still-older events.
//   - `hasRunning` gates the live-tail poll: while a run is executing we
//     refresh the newest page every 5s; once nothing is running we stop.
//   - `message` is surfaced as the empty state when there are no lines
//     yet (e.g. "No tasks have started yet").
//
// The `status` prop is the pipeline's bundleStatus. We DON'T hard-gate
// "no logs" on a sleeping/stopped bundle — workflow container logs persist
// in CloudWatch across sleeps, so the empty state shows only when the API
// genuinely returns zero lines.

const WORKFLOW_LOGS_POLL_MS = 5000;
const WORKFLOW_LOGS_PAGE = 200;

function formatWorkflowLogTime(ts) {
  if (ts == null) return '';
  try {
    // CloudWatch event timestamps are epoch MILLISECONDS. Coerce to a number
    // so a numeric STRING ("1749...") doesn't get mis-parsed by Date's
    // string path (which would yield UTC/Invalid). toLocaleTimeString then
    // renders in the BROWSER's local timezone.
    const ms = typeof ts === 'number' ? ts : Number(ts);
    const d = new Date(Number.isFinite(ms) ? ms : ts);
    if (Number.isNaN(d.getTime())) return '';
    // Include the DATE (browser-local), not just the time. The Logs tab can
    // interleave a days-old execution with today's warm-runner output; with a
    // time-only gutter a "05-31 11:49" log looks like a FUTURE "11:49" today.
    const date = d.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' });
    const time = d.toLocaleTimeString(undefined, {
      hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    return `${date} ${time}.${String(d.getMilliseconds()).padStart(3, '0')}`;
  } catch {
    return '';
  }
}

// Stable key for a log line — CloudWatch can return overlapping events
// across polls, so key on (timestamp, jobId, message) rather than index
// to keep React reconciliation honest when we prepend older pages.
function workflowLogKey(line, i) {
  return `${line.timestamp || ''}:${line.jobId || ''}:${i}`;
}

const WorkflowLogsTab = ({ projectId, workflowType, status }) => {
  // `lines` is the flat buffer of { timestamp, message, jobId } objects,
  // always chronological (oldest at index 0, newest at the bottom).
  // `hasOlder` says whether scrolling up will yield events EARLIER than
  // lines[0]; the older-page request is keyed off lines[0].timestamp
  // (oldestTsRef), not a server cursor. Live-tail polling only runs while
  // the user is anchored at the bottom (stickyBottomRef) AND a run is
  // executing (hasRunningRef) — once they scroll up to read history we
  // freeze the buffer so their viewport doesn't get stomped.
  const [lines, setLines] = useState([]);
  const [hasOlder, setHasOlder] = useState(false);     // older events exist?
  const [hasRunning, setHasRunning] = useState(false); // any run executing?
  const [emptyMessage, setEmptyMessage] = useState(''); // API `message` (no streams yet)
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState(null);
  // Skeleton gate — true once the first fetch resolves (success or error).
  // Subsequent polls / load-older fetches don't flip it back, so the
  // skeleton only flashes on initial mount.
  const [hasLoaded, setHasLoaded] = useState(false);

  const bodyRef = useRef(null);
  const stickyBottomRef = useRef(true);
  // Refs mirror state for callbacks (scroll handler, polling loop) that
  // close over the initial render's values.
  const hasOlderRef = useRef(hasOlder);
  const loadingOlderRef = useRef(loadingOlder);
  const hasRunningRef = useRef(hasRunning);
  // Oldest currently-loaded event timestamp — the cursor for "load older"
  // (we ask the backend for the page strictly EARLIER than this). Kept in a
  // ref so the scroll/poll callbacks read the live value without re-binding.
  const oldestTsRef = useRef(null);
  useEffect(() => { hasOlderRef.current = hasOlder; }, [hasOlder]);
  useEffect(() => { loadingOlderRef.current = loadingOlder; }, [loadingOlder]);
  useEffect(() => { hasRunningRef.current = hasRunning; }, [hasRunning]);
  // Track the oldest loaded timestamp off `lines` so both the initial/live
  // replace and the load-older prepend keep the cursor in sync.
  useEffect(() => {
    oldestTsRef.current = lines.length ? lines[0].timestamp : null;
  }, [lines]);

  // Initial load + live-tail poll. The first fetch always runs so the
  // panel populates; subsequent interval ticks honour the stickyBottom
  // gate (don't clobber a user reading history) and stop entirely once
  // nothing is running. The pipeline being "active" (bundleStatus) is
  // NOT a polling gate — `hasRunning` from the API is the source of truth
  // for whether new lines can still arrive.
  useEffect(() => {
    if (!projectId || !workflowType) return undefined;
    let cancelled = false;
    let firstFetchDone = false;
    let timer = null;

    const fetchLatest = async () => {
      const node = bodyRef.current;
      if (node) {
        const slack = 24;
        stickyBottomRef.current =
          (node.scrollHeight - node.scrollTop - node.clientHeight) <= slack;
      }
      // After the first fetch, don't replace the buffer if the user has
      // scrolled up — fetchLatest pulls only the newest page, which would
      // wipe any older pages they loaded and jump the viewport.
      if (firstFetchDone && !stickyBottomRef.current) {
        schedule();
        return;
      }
      try {
        const data = await WorkflowLogs.getWorkflowLogs(projectId, workflowType, {
          limit: WORKFLOW_LOGS_PAGE,
        });
        if (cancelled) return;
        // Newest window — backend returns lines chronological (oldest→newest)
        // so the newest line sits at the bottom of the buffer. This REPLACES
        // the buffer (only runs when sticky-bottom), so any older pages the
        // user loaded are intentionally dropped to re-anchor on the tail.
        const next = Array.isArray(data?.lines) ? data.lines : [];
        setLines(next);
        setHasOlder(!!data?.hasOlder);
        setHasRunning(!!data?.hasRunning);
        setEmptyMessage(next.length === 0 ? (data?.message || '') : '');
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Failed to fetch logs');
      } finally {
        if (!cancelled) {
          setHasLoaded(true);
          firstFetchDone = true;
          schedule();
        }
      }
    };

    // Re-arm the poll only while a run is still executing. Once hasRunning
    // is false the loop goes quiet (no fixed interval) — the tab will pick
    // back up on remount / tab re-entry.
    function schedule() {
      if (cancelled) return;
      if (timer) { clearTimeout(timer); timer = null; }
      if (hasRunningRef.current) {
        timer = setTimeout(fetchLatest, WORKFLOW_LOGS_POLL_MS);
      }
    }

    fetchLatest();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [projectId, workflowType]);

  // Load-older — prepend the next older page and restore scroll position
  // so the viewport stays anchored on the line the user was reading.
  // Guarded by hasOlder + loadingOlder to avoid duplicate fan-out.
  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current) return;
    if (!hasOlderRef.current) return;
    // Need a reference timestamp to page BEFORE. If the buffer is somehow
    // empty there's nothing older to anchor to.
    const before = oldestTsRef.current;
    if (before == null) return;
    setLoadingOlder(true);
    const node = bodyRef.current;
    const prevScrollHeight = node ? node.scrollHeight : 0;
    const prevScrollTop = node ? node.scrollTop : 0;
    try {
      // Fetch the page of events strictly EARLIER than the oldest line we
      // already have, then PREPEND it — so scroll-up always yields OLDER
      // timestamps above the current top, never newer.
      const data = await WorkflowLogs.getWorkflowLogs(projectId, workflowType, {
        limit: WORKFLOW_LOGS_PAGE,
        before,
      });
      const older = Array.isArray(data?.lines) ? data.lines : [];
      // Guard against any boundary overlap: drop events that aren't strictly
      // older than our current oldest (CloudWatch endTime is exclusive, but
      // belt-and-suspenders keeps the buffer strictly increasing).
      const strictlyOlder = older.filter((l) => l.timestamp < before);
      if (strictlyOlder.length > 0) {
        setLines((prev) => [...strictlyOlder, ...prev]);
      }
      setHasOlder(!!data?.hasOlder && strictlyOlder.length > 0);
      requestAnimationFrame(() => {
        const n2 = bodyRef.current;
        if (!n2) return;
        const delta = n2.scrollHeight - prevScrollHeight;
        n2.scrollTop = prevScrollTop + delta;
      });
    } catch (err) {
      setError(err?.message || 'Failed to fetch older logs');
    } finally {
      setLoadingOlder(false);
    }
  }, [projectId, workflowType]);

  // Scroll-position detector — within 50px of the top requests the next
  // older page.
  useEffect(() => {
    const node = bodyRef.current;
    if (!node) return undefined;
    const onScroll = () => {
      // Keep the sticky-bottom flag CURRENT on every scroll. Without this it
      // only updates during a poll, so scrolling up to read history leaves it
      // stale-true — and the next `lines` change (a load-older prepend or a
      // 5s live-tail tick) makes the auto-scroll effect yank the viewport back
      // down. Updating it here means "scrolled up" → sticky=false → no jump.
      const slack = 24;
      stickyBottomRef.current =
        (node.scrollHeight - node.scrollTop - node.clientHeight) <= slack;
      if (node.scrollTop < 50) loadOlder();
    };
    node.addEventListener('scroll', onScroll);
    return () => node.removeEventListener('scroll', onScroll);
  }, [loadOlder]);

  // Auto-scroll to bottom on live-tail updates only when the user is
  // already near the bottom (sticky-tail). If they've scrolled up to
  // browse history, leave them put.
  useEffect(() => {
    if (!stickyBottomRef.current) return;
    const node = bodyRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [lines]);

  const gutterWidth = Math.max(2, String(lines.length).length);

  return (
    <div className="pipeline-tab">
      <div className="page-card app-detail-card">
        <div className="card-header app-logs-header">
          <span className="pipeline-section-hint" style={{ margin: 0 }}>
            Container logs for this workflow — includes warm-runner warm-up
            as well as each run's execution output.
          </span>
          <div className="app-logs-controls">
            {hasRunning && (
              <span className="app-logs-live" title="A run is executing — polling every 5s">
                Live
              </span>
            )}
          </div>
        </div>

        <pre ref={bodyRef} className="app-logs-body wf-logs-body">
          {/* Top sentinel — load-older indicator / end-of-history mark. */}
          {hasLoaded && lines.length > 0 && (
            <div className="app-logs-loader">
              {loadingOlder
                ? 'Loading older lines…'
                : (hasOlder ? 'Scroll up to load older lines…' : '— End of history —')}
            </div>
          )}
          {!hasLoaded ? (
            // Initial-load skeleton — same eight shimmer rows the apps
            // LogsTab uses so there's no layout shift when logs paint.
            <div className="app-logs-skeleton" aria-hidden>
              {[66, 82, 74, 90, 58, 78, 86, 64].map((w, i) => (
                <div key={i} className="app-logs-line app-logs-skel-line">
                  <span
                    className="app-logs-lineno app-logs-skel-lineno"
                    style={{ width: '3ch' }}
                  />
                  <span
                    className="app-logs-linetext app-logs-skel-bar"
                    style={{ width: `${w}%` }}
                  />
                </div>
              ))}
            </div>
          ) : error ? (
            <span className="app-logs-error">⚠ {error}</span>
          ) : lines.length === 0 ? (
            <span className="app-logs-empty">
              {emptyMessage
                || 'No tasks have started yet — no logs available.'}
            </span>
          ) : (
            lines.map((line, i) => {
              const ts = formatWorkflowLogTime(line.timestamp);
              // Parse the raw container message for level/severity so the
              // workflow logs highlight identically to the apps logs.
              // `sniffPlain` is on because workflow messages are plain stdout
              // (not JSON-wrapped), so "Warning"/"error" must be sniffed from
              // the text. The whole message is the primary line, so it goes in
              // the bright `msg` slot (with any JSON body kept dimmed after).
              const p = parseLogPayload(line.message, { sniffPlain: true });
              return (
                <LogLine
                  key={workflowLogKey(line, i)}
                  lineNo={i + 1}
                  gutterWidth={gutterWidth}
                  ts={ts}
                  level={p.level}
                  msg={p.msg || p.body}
                  body={p.msg ? p.body : null}
                  severity={p.severity}
                />
              );
            })
          )}
        </pre>
      </div>
    </div>
  );
};

export default PipelineDetailPage;

// Test-only exports — kept under a __test_only_* prefix so any non-test
// consumer immediately stands out. The Settings tab's WarmPoolSection
// is exported so the smoke test can mount it without dragging in the
// full router + outlet context the page needs.
export { WarmPoolSection as __test_only_WarmPoolSection };
